<template>
  <div class="whatsapp-layout">
    <!-- Sidebar -->
    <Sidebar :isAgent="isAgent" />

    <!-- Main Content -->
    <div class="main-content">
      <!-- Top Header -->
      <TopHeader />

      <!-- Page Content -->
      <main class="page-content">
        <!-- WhatsApp Not Configured Overlay -->
        <div v-if="hasWhatsAppCredentials === false" class="whatsapp-disabled-overlay">
          <div class="disabled-overlay-content">
            <img
              src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg"
              alt="WhatsApp"
              class="disabled-whatsapp-icon"
            />
            <h2 class="disabled-overlay-title">WhatsApp Not Configured</h2>
            <p class="disabled-overlay-message">
              You need to set up your WhatsApp Business integration before you can access conversations.
            </p>
            <button @click="goToWhatsAppTutorial" class="setup-whatsapp-btn">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" style="margin-right: 8px;">
                <path d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4M11,16.5L6.5,12L7.91,10.59L11,13.67L16.59,8.09L18,9.5L11,16.5Z"/>
              </svg>
              <span>Set Up WhatsApp</span>
            </button>
          </div>
        </div>

        <div class="whatsapp-container" :class="{ 'split-view': selectedConversation, 'mobile-conversation-view': selectedConversation, 'blurred': hasWhatsAppCredentials === false }">
          <!-- Left Panel: Conversations List -->
          <div class="conversations-panel" :class="{ 'mobile-hidden': selectedConversation }">
            <!-- Filters and Search -->
            <div class="filters-container">
              <div class="search-box">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" class="search-icon">
                  <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                </svg>
                <input
                  v-model="searchQuery"
                  @input="handleSearch"
                  type="text"
                  placeholder="Search conversations..."
                  class="search-input"
                />
              </div>

              <div class="filter-controls">
                <button
                  @click="toggleNotifications"
                  class="notification-toggle-btn"
                  :class="{ 'active': notificationEnabled }"
                  :title="notificationEnabled ? 'Notifications ON - Click to turn OFF' : 'Notifications OFF - Click to turn ON'"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path v-if="notificationEnabled" d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"/>
                    <path v-else d="M20 18.69L7.84 6.14 5.27 3.49 4 4.76l2.8 2.8v.01c-.52.99-.8 2.16-.8 3.42v5l-2 2v1h13.73l2 2L21 19.73l-1-1.04zM12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-7.32V11c0-3.08-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68c-.15.03-.29.08-.42.12-.1.03-.2.07-.3.11h-.01c-.01 0-.01 0-.02.01-.23.09-.46.2-.68.31 0 0-.01 0-.01.01L18 14.68z"/>
                  </svg>
                </button>

                <select v-model="filters.needs_agent" @change="loadConversations" class="filter-select">
                  <option value="">All Conversations</option>
                  <option value="1">Lead / Needs Agent</option>
                  <option value="0">Handled</option>
                </select>

                <!-- Department Filter (Only visible for owners when department mode is enabled) -->
                <select
                  v-if="!isAgent && departmentModeEnabled"
                  v-model="filters.department_id"
                  @change="loadConversations"
                  class="filter-select department-filter"
                >
                  <option value="">All Departments</option>
                  <option v-for="dept in departments" :key="dept.id" :value="dept.id">
                    {{ dept.name }}
                  </option>
                </select>

                <button
                  @click="toggleUnread"
                  class="unread-toggle-btn"
                  :class="{ active: filters.unread_only === 1 }"
                  :title="filters.unread_only === 1 ? 'Show all conversations' : 'Show unread only'"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                  </svg>
                  <span class="unread-text">
                    {{ filters.unread_only === 1 ? 'Unread' : 'Unread' }}
                  </span>
                </button>

                <button
                  @click="toggleArchived"
                  class="archive-toggle-btn"
                  :class="{ active: filters.archived === 1 }"
                  :title="filters.archived === 1 ? 'Show active conversations' : 'Show archived conversations'"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.54 5.23l-1.39-1.68C18.88 3.21 18.47 3 18 3H6c-.47 0-.88.21-1.16.55L3.46 5.23C3.17 5.57 3 6.02 3 6.5V19c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6.5c0-.48-.17-.93-.46-1.27zM6.24 5h11.52l.81.97H5.44l.8-.97zM5 19V8h14v11H5zm8.45-9L12 8.55 10.55 10H9.5v1h5v-1h-1.05z"/>
                  </svg>
                  <span class="archive-text">
                    {{ filters.archived === 1 ? 'Archived' : 'Archive' }}
                  </span>
                </button>
              </div>
            </div>

            <!-- Conversations List -->
            <div class="conversations-list-container" @scroll="handleConversationsScroll">
              <div v-if="loading" class="loading-state">
                <div class="loading-spinner"></div>
                <p>Loading WhatsApp conversations...</p>
              </div>

              <div v-else-if="conversations.length === 0" class="empty-state">
                <div class="empty-icon">💬</div>
                <h3>No conversations found</h3>
                <p>No WhatsApp conversations match your current filters</p>
              </div>

              <div v-else class="conversations-list">
                <div
                  v-for="conversation in conversations"
                  :key="conversation.id"
                  class="conversation-card"
                  :class="{
                    'unread': conversation.read === 0 || conversation.read === false || !conversation.read,
                    'needs-agent': conversation.needs_agent === 1 || conversation.needs_agent === true,
                    'vip': conversation.vip === 1 || conversation.vip === true,
                    'blocked': conversation.blocked === 1 || conversation.blocked === true,
                    'selected': selectedConversation && selectedConversation.id === conversation.id
                  }"
                  @click="selectConversation(conversation)"
                  @contextmenu.prevent="openContextMenu($event, conversation)"
                >
                <!-- WhatsApp Avatar -->
                <div class="conversation-avatar">
                  <div class="avatar-circle whatsapp">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.56-.01-.188 0-.495.074-.754.372-.26.297-.99.967-.99 2.357 0 1.388 1.014 2.731 1.155 2.921.142.19 2.006 3.045 4.848 4.272.678.292 1.207.466 1.621.596.682.217 1.303.187 1.793.113.547-.081 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zm-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.051 3.488z"/>
                    </svg>
                  </div>
                  <div v-if="conversation.vip" class="vip-badge">VIP</div>
                </div>

                <!-- Conversation Content -->
                <div class="conversation-content">
                  <div class="conversation-header">
                    <h4 class="contact-name">{{ conversation.from }}</h4>
                    <div class="conversation-meta">
                      <span v-if="conversation.read === 0 || conversation.read === false || !conversation.read" class="unread-indicator"></span>
                      <span class="timestamp">{{ conversation.date_time }}</span>
                    </div>
                  </div>

                  <div class="last-message">
                    <span v-if="conversation.image_url" class="media-indicator">
                      📷 Photo
                    </span>
                    <span v-else-if="conversation.video_url" class="media-indicator">
                      🎥 Video
                    </span>
                    <span v-else-if="conversation.audio_url" class="media-indicator">
                      🎤 Voice Note
                    </span>
                    <span v-else-if="conversation.sticker_url" class="media-indicator">
                      🎨 Sticker
                    </span>
                    <span v-else-if="conversation.document_url" class="media-indicator">
                      📄 Document
                    </span>
                    <span v-else class="message-text">{{ conversation.message }}</span>
                  </div>

                  <!-- Additional WhatsApp Info -->
                  <div class="whatsapp-meta">
                    <div class="meta-row">
                      <span class="meta-label">Customer ID:</span>
                      <span class="meta-value">{{ conversation.customer_id }}</span>
                    </div>
                    <div class="meta-row" v-if="conversation.agent_name">
                      <span class="meta-label">Agent:</span>
                      <span class="meta-value">{{ conversation.agent_name }}</span>
                    </div>
                    <div class="meta-row">
                      <span class="meta-label">WhatsApp ID:</span>
                      <span class="meta-value">{{ conversation.wa_id }}</span>
                    </div>
                  </div>
                </div>

                <!-- Status Indicators -->
                <div class="status-indicators">
                  <div v-if="conversation.blocked === 1 || conversation.blocked === true" class="status-badge blocked-badge">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12,2C6.48,2 2,6.48 2,12C2,17.52 6.48,22 12,22C17.52,22 22,17.52 22,12C22,6.48 17.52,2 12,2M4,12C4,7.58 7.58,4 12,4C13.85,4 15.55,4.63 16.9,5.69L5.69,16.9C4.63,15.55 4,13.85 4,12M12,20C10.15,20 8.45,19.37 7.1,18.31L18.31,7.1C19.37,8.45 20,10.15 20,12C20,16.42 16.42,20 12,20Z"/>
                    </svg>
                    Blocked
                  </div>
                  <div v-if="conversation.needs_agent === 1 || conversation.needs_agent === true" class="status-badge needs-agent-badge">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" class="bell-icon">
                      <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"/>
                    </svg>
                    <div class="notification-dot"></div>
                    Lead / Needs Agent
                  </div>
                  <div v-if="conversation.vip === 1 || conversation.vip === true" class="status-badge vip-badge-main">
                    VIP
                  </div>
                </div>
              </div>
              </div>

              <!-- Load More Indicator -->
              <div v-if="loadingMore" class="load-more-indicator">
                <div class="load-more-spinner"></div>
              </div>
            </div>

            <!-- Context Menu -->
            <div
              v-if="contextMenu.show"
              class="context-menu"
              :style="{ top: contextMenu.y + 'px', left: contextMenu.x + 'px' }"
              @click="closeContextMenu"
            >
              <div class="context-menu-item" @click="toggleReadStatus(contextMenu.conversation)">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                </svg>
                <span>{{ (contextMenu.conversation && (contextMenu.conversation.read === 0 || contextMenu.conversation.read === false || !contextMenu.conversation.read)) ? 'Mark as Read' : 'Mark as Unread' }}</span>
              </div>
              <div class="context-menu-item" @click="toggleArchiveFromContext(contextMenu.conversation)">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.54 5.23l-1.39-1.68C18.88 3.21 18.47 3 18 3H6c-.47 0-.88.21-1.16.55L3.46 5.23C3.17 5.57 3 6.02 3 6.5V19c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6.5c0-.48-.17-.93-.46-1.27zM6.24 5h11.52l.81.97H5.44l.8-.97zM5 19V8h14v11H5z"/>
                </svg>
                <span>{{ (contextMenu.conversation && (contextMenu.conversation.archived === 1 || contextMenu.conversation.archived === true)) ? 'Unarchive' : 'Archive' }}</span>
              </div>
              <!-- Transfer Department (Only show if department mode enabled) -->
              <div v-if="departmentModeEnabled" class="context-menu-item" @click="openTransferModal(contextMenu.conversation)">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M10,17L15,12L10,7V17Z"/>
                </svg>
                <span>Transfer to Department</span>
              </div>
            </div>
          </div>

          <!-- Transfer Department Modal -->
          <div v-if="showTransferModal" class="modal-overlay" @click="closeTransferModal">
            <div class="modal-content transfer-modal" @click.stop>
              <div class="modal-header">
                <h3>Transfer Customer to Department</h3>
                <button @click="closeTransferModal" class="close-modal-btn">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"/>
                  </svg>
                </button>
              </div>

              <div class="modal-body">
                <p class="transfer-info">
                  Select a department to transfer this customer to:
                </p>

                <div class="form-group">
                  <label>Customer</label>
                  <div class="customer-info">
                    <strong>{{ transferData.customerName || transferData.customerPhone }}</strong>
                    <span class="customer-phone">{{ transferData.customerPhone }}</span>
                  </div>
                </div>

                <div v-if="transferData.currentDepartmentName" class="form-group">
                  <label>Current Department</label>
                  <div class="current-department-info">
                    {{ transferData.currentDepartmentName }}
                  </div>
                </div>

                <div class="form-group">
                  <label for="transfer-department">Transfer To Department <span class="required">*</span></label>
                  <select
                    id="transfer-department"
                    v-model="transferData.newDepartmentId"
                    class="form-control"
                  >
                    <option value="">Select Department</option>
                    <option
                      v-for="dept in allDepartments"
                      :key="dept.id"
                      :value="dept.id"
                      :disabled="dept.id === transferData.currentDepartmentId"
                    >
                      {{ dept.name }}{{ dept.id === transferData.currentDepartmentId ? ' (Current)' : '' }}
                    </option>
                  </select>
                </div>

                <div class="form-group">
                  <label for="transfer-language">Message Language <span class="required">*</span></label>
                  <select
                    id="transfer-language"
                    v-model="transferData.language"
                    class="form-control"
                  >
                    <option value="ar">Arabic</option>
                    <option value="en">English</option>
                  </select>
                </div>

                <div v-if="transferError" class="error-message">
                  {{ transferError }}
                </div>
              </div>

              <div class="modal-footer">
                <button @click="closeTransferModal" class="btn btn-secondary">Cancel</button>
                <button
                  @click="confirmTransfer"
                  class="btn btn-primary"
                  :disabled="!transferData.newDepartmentId || transferLoading"
                >
                  <span v-if="transferLoading">Transferring...</span>
                  <span v-else>Transfer Customer</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Right Panel: Conversation Detail -->
          <div v-if="selectedConversation" class="conversation-detail-panel" :class="{ 'mobile-full-width': selectedConversation }">
            <div class="conversation-detail-header">
              <button @click="closeConversation" class="mobile-back-btn">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20,11V13H8L13.5,18.5L12.08,19.92L4.16,12L12.08,4.08L13.5,5.5L8,11H20Z"/>
                </svg>
              </button>
              <div class="header-info">
                <div class="contact-avatar">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.56-.01-.188 0-.495.074-.754.372-.26.297-.99.967-.99 2.357 0 1.388 1.014 2.731 1.155 2.921.142.19 2.006 3.045 4.848 4.272.678.292 1.207.466 1.621.596.682.217 1.303.187 1.793.113.547-.081 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zm-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.051 3.488z"/>
                  </svg>
                </div>
                <div class="contact-details">
                  <h3 class="contact-name">{{ selectedConversation.from || 'Unknown Contact' }}</h3>
                  <p class="contact-id">WhatsApp ID: {{ selectedConversation.wa_id }}</p>

                  <!-- Live Viewers Indicator -->
                  <div v-if="liveViewers.length > 0" class="live-viewers-indicator">
                    <div class="viewer-avatars">
                      <div
                        v-for="viewer in liveViewers"
                        :key="viewer.id"
                        class="viewer-avatar"
                        :title="`${viewer.name} (${viewer.role})`"
                      >
                        {{ viewer.name.charAt(0).toUpperCase() }}
                      </div>
                    </div>
                    <span class="viewer-count">{{ liveViewers.length }} viewing</span>
                  </div>
                </div>
              </div>
              <div class="header-actions">
                <button v-if="notesCount !== null" @click="openNotesModal" class="notes-btn" title="Customer Notes">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M14,17H7V15H14M17,13H7V11H17M17,9H7V7H17M19,3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3Z"/>
                  </svg>
                  <span class="notes-badge" v-if="notesCount > 0">{{ notesCount }}</span>
                </button>
                <button @click="toggleMediaGallery" class="media-gallery-btn" title="View Media Gallery">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M22,16V4A2,2 0 0,0 20,2H8A2,2 0 0,0 6,4V16A2,2 0 0,0 8,18H20A2,2 0 0,0 22,16M11,12L13.03,14.71L16,11L20,16H8M2,6V20A2,2 0 0,0 4,22H18V20H4V6"/>
                  </svg>
                  <span class="media-badge" v-if="mediaGalleryTotals.total > 0">{{ mediaGalleryTotals.total }}</span>
                </button>
                <button
                  @click="openCreateLeadModal"
                  class="create-lead-btn"
                  :class="{
                    'has-lead': conversationHasLead,
                    'lead-complete': leadIsComplete
                  }"
                  :title="leadIsComplete ? 'Lead Complete - View/Edit' : (conversationHasLead ? 'Update Lead' : 'Create Lead')"
                >
                  <!-- Add person icon for new leads -->
                  <svg v-if="!conversationHasLead" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M15,14C12.33,14 7,15.33 7,18V20H23V18C23,15.33 17.67,14 15,14M6,10V7H4V10H1V12H4V15H6V12H9V10M15,12A4,4 0 0,0 19,8A4,4 0 0,0 15,4A4,4 0 0,0 11,8A4,4 0 0,0 15,12Z"/>
                  </svg>
                  <!-- Checkmark icon for complete leads -->
                  <svg v-else-if="leadIsComplete" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"/>
                  </svg>
                  <!-- Edit icon for incomplete leads -->
                  <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18,2.9 17.35,2.9 16.96,3.29L15.12,5.12L18.87,8.87M3,17.25V21H6.75L17.81,9.93L14.06,6.18L3,17.25Z"/>
                  </svg>
                  <span class="create-lead-text">
                    {{ leadIsComplete ? 'Lead Complete' : (conversationHasLead ? 'Update Lead' : 'Create Lead') }}
                  </span>
                </button>
                <button
                  v-if="chatbotStatus !== null"
                  @click="toggleChatbot"
                  class="chatbot-btn"
                  :class="{ 'active': chatbotStatus?.bot_status }"
                  :disabled="loadingChatbot"
                  :title="chatbotStatus?.bot_status ? 'Chatbot is ON - Click to turn OFF' : 'Chatbot is OFF - Click to turn ON'"
                >
                  <div v-if="loadingChatbot" class="loading-spinner-small"></div>
                  <svg v-else width="24" height="24" viewBox="0 0 24 24" fill="none" stroke-width="1.8">
                    <!-- Robot head -->
                    <rect x="6" y="6" width="12" height="10" rx="3" fill="#FFC107" fill-opacity="0.15" stroke="#FFC107" stroke-width="1.5"/>

                    <!-- Antenna -->
                    <line x1="12" y1="2" x2="12" y2="6" stroke="#FFC107"/>
                    <circle cx="12" cy="2" r="1" fill="#FFC107"/>

                    <!-- Eyes -->
                    <circle cx="9.5" cy="10" r="1.5" fill="#3B82F6"/>
                    <circle cx="14.5" cy="10" r="1.5" fill="#3B82F6"/>

                    <!-- Mouth -->
                    <path d="M9 13.5C10 14.5 11.5 14.5 12 14.5C12.5 14.5 14 14.5 15 13.5" stroke="#FFC107" stroke-linecap="round"/>

                    <!-- Body -->
                    <rect x="8" y="16" width="8" height="6" rx="2" fill="#FFC107" fill-opacity="0.1" stroke="#FFC107" stroke-width="1.5"/>

                    <!-- Arms -->
                    <rect x="4" y="17" width="2" height="4" rx="1" fill="#FFC107" fill-opacity="0.08" stroke="#FFC107"/>
                    <rect x="18" y="17" width="2" height="4" rx="1" fill="#FFC107" fill-opacity="0.08" stroke="#FFC107"/>

                    <!-- Control panel dots -->
                    <circle cx="10" cy="19" r="0.5" fill="#3B82F6"/>
                    <circle cx="12" cy="19" r="0.5" fill="#3B82F6"/>
                    <circle cx="14" cy="19" r="0.5" fill="#3B82F6"/>
                  </svg>
                </button>
                <button
                  v-if="selectedConversation"
                  @click="toggleBlockContact"
                  class="block-btn"
                  :class="{ 'blocked': selectedConversation.blocked === 1 || selectedConversation.blocked === true }"
                  :disabled="loadingBlock"
                  :title="(selectedConversation.blocked === 1 || selectedConversation.blocked === true) ? 'Unblock Contact' : 'Block Contact'"
                >
                  <div v-if="loadingBlock" class="loading-spinner-small"></div>
                  <template v-else>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12,2C6.48,2 2,6.48 2,12C2,17.52 6.48,22 12,22C17.52,22 22,17.52 22,12C22,6.48 17.52,2 12,2M4,12C4,7.58 7.58,4 12,4C13.85,4 15.55,4.63 16.9,5.69L5.69,16.9C4.63,15.55 4,13.85 4,12M12,20C10.15,20 8.45,19.37 7.1,18.31L18.31,7.1C19.37,8.45 20,10.15 20,12C20,16.42 16.42,20 12,20Z"/>
                    </svg>
                  </template>
                </button>
                <button
                  v-if="selectedConversation && canSendTemplates"
                  @click="openQuickSendTemplateModal"
                  class="quick-send-template-btn"
                  title="Send WhatsApp Template (for 24h+ conversations)"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17,12V3A1,1 0 0,0 16,2H3A1,1 0 0,0 2,3V17L6,13H16A1,1 0 0,0 17,12M21,6H19V15H6V17A1,1 0 0,0 7,18H18L22,22V7A1,1 0 0,0 21,6Z"/>
                  </svg>
                  <span class="quick-send-text">Quick Send Template</span>
                </button>
                <button
                  v-if="selectedConversation && !isAgent"
                  @click="analyzeChat"
                  class="analyze-btn"
                  :disabled="loadingAnalysis"
                  title="Analyze Chat Conversation"
                >
                  <div v-if="loadingAnalysis" class="loading-spinner-small"></div>
                  <template v-else>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19,3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.11,3 19,3M19,19H5V5H19M9.5,11A0.5,0.5 0 0,0 9,11.5V12.5A0.5,0.5 0 0,0 9.5,13H11V15H6.5A0.5,0.5 0 0,0 6,15.5V16.5A0.5,0.5 0 0,0 6.5,17H13V15.5A0.5,0.5 0 0,0 12.5,15H12V13H17.5A0.5,0.5 0 0,0 18,12.5V11.5A0.5,0.5 0 0,0 17.5,11H9.5M7,9V7H17V9H7Z"/>
                    </svg>
                    <span class="analyze-text">Analyze Conversation</span>
                  </template>
                </button>
                <button @click="closeConversation" class="close-conversation-btn">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                  </svg>
                </button>
              </div>
            </div>

            <div class="conversation-messages-container">
              <div v-if="loadingMessages" class="messages-loading">
                <div class="loading-spinner"></div>
                <p>Loading conversation messages...</p>
              </div>

              <div v-else-if="conversationMessages.length === 0" class="no-messages">
                <div class="no-messages-icon">💬</div>
                <p>No messages found in this conversation</p>
              </div>

              <div v-else class="messages-list">
                <div
                  v-for="message in conversationMessages"
                  :key="message.id"
                  class="message-item"
                  :class="{
                    'message-agent': message.conversation_role === 'Agent',
                    'message-owner': message.conversation_role === 'Owner',
                    'message-customer': message.conversation_role === 'Customer',
                    'message-pending': message._optimistic
                  }"
                >
                  <div class="message-header">
                    <span class="message-sender">{{ message.from }}</span>
                    <!-- Reply Button -->
                    <button
                      v-if="message.wa_message_id"
                      @click="setReplyTo(message)"
                      class="reply-btn"
                      title="Reply to message"
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M10 9V5l-7 7 7 7v-4.1c5 0 8.5 1.6 11 5.1-1-5-4-10-11-11z"/>
                      </svg>
                      <span>Reply</span>
                    </button>
                    <!-- Quick Add To Notes Button -->
                    <button
                      v-if="message.message"
                      @click="quickAddToNotes(message)"
                      class="quick-note-btn"
                      title="Quick Add To Notes"
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-2 10h-4v4h-2v-4H7v-2h4V7h2v4h4v2z"/>
                      </svg>
                      <span>Quick Add Note</span>
                    </button>
                    <!-- Forward Button - Top Right -->
                    <button @click="openForwardModal(message)" class="forward-btn" title="Forward message">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                      </svg>
                      <span>Forward</span>
                    </button>
                  </div>
                  <div class="message-content">
                    <!-- Quoted reply context -->
                    <div
                      v-if="message.reply_to_message_id"
                      class="quoted-message"
                      :class="getQuotedMessage(message.reply_to_message_id) && getQuotedMessage(message.reply_to_message_id).conversation_role === 'Customer' ? 'quoted-customer' : 'quoted-owner'"
                    >
                      <span class="quoted-sender">{{ getQuotedMessage(message.reply_to_message_id) ? getQuotedMessage(message.reply_to_message_id).from : 'Replied message' }}</span>
                      <span class="quoted-preview">{{ getQuotedPreview(message.reply_to_message_id) }}</span>
                    </div>
                    <!-- Image -->
                    <div v-if="message.image_url" class="message-image">
                      <img
                        :src="message.image_url"
                        alt="Message image"
                        class="message-img"
                        @click="openImageViewer(message.image_url)"
                      />
                    </div>
                    <!-- Video -->
                    <div v-if="message.video_url" class="message-video">
                      <video controls class="message-vid">
                        <source :src="message.video_url" type="video/mp4">
                        Your browser does not support the video tag.
                      </video>
                    </div>
                    <!-- Audio (Voice Note) -->
                    <div v-if="message.audio_url" class="message-audio">
                      <div class="voice-note-player">
                        <!-- Play/Pause Button -->
                        <button
                          @click="toggleAudio(message.id, message.audio_url)"
                          class="voice-play-btn"
                          :class="{ 'playing': playingAudioId === message.id }"
                        >
                          <svg v-if="playingAudioId === message.id" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                          </svg>
                          <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M8 5v14l11-7z"/>
                          </svg>
                        </button>

                        <!-- Waveform Visualization -->
                        <div class="voice-waveform">
                          <div class="waveform-bar" v-for="n in 30" :key="n" :style="{ height: getWaveformHeight(n) + '%' }"></div>
                        </div>

                        <!-- Duration -->
                        <span class="voice-duration">{{ getAudioDuration(message.id) }}</span>

                        <!-- Hidden audio element -->
                        <audio
                          :ref="`audio-${message.id}`"
                          :src="message.audio_url"
                          @loadedmetadata="onAudioLoaded(message.id)"
                          @timeupdate="onAudioTimeUpdate(message.id)"
                          @ended="onAudioEnded(message.id)"
                          style="display: none;"
                        >
                          <source :src="message.audio_url" type="audio/wav">
                          <source :src="message.audio_url" type="audio/ogg">
                          <source :src="message.audio_url" type="audio/mpeg">
                        </audio>
                      </div>
                    </div>
                    <!-- Sticker -->
                    <div v-if="message.sticker_url" class="message-sticker">
                      <img :src="message.sticker_url" alt="Sticker" class="message-sticker-img" />
                    </div>
                    <!-- Document -->
                    <div v-if="message.document_url" class="message-document">
                      <a :href="message.document_url" target="_blank" class="document-link">
                        <div class="document-icon">
                          <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
                          </svg>
                        </div>
                        <div class="document-info-message">
                          <span class="document-filename">{{ getDocumentName(message.document_url) }}</span>
                          <span class="document-action">Click to download</span>
                        </div>
                      </a>
                    </div>
                    <!-- Text message -->
                    <p v-if="message.message" class="message-text" v-html="formatMessageText(message.message)"></p>
                    <!-- Timestamp - Bottom Right -->
                    <span class="message-time">{{ message.date_time }}</span>
                  </div>
                  <div class="message-footer">
                    <div v-if="message.agent_name" class="message-meta">
                      <span class="agent-info">Agent: {{ message.agent_name }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Message Input Form -->
            <div class="message-input-container">
              <!-- Blocked Contact Warning -->
              <div v-if="selectedConversation && (selectedConversation.blocked === 1 || selectedConversation.blocked === true)" class="blocked-contact-warning">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12,2C6.48,2 2,6.48 2,12C2,17.52 6.48,22 12,22C17.52,22 22,17.52 22,12C22,6.48 17.52,2 12,2M4,12C4,7.58 7.58,4 12,4C13.85,4 15.55,4.63 16.9,5.69L5.69,16.9C4.63,15.55 4,13.85 4,12M12,20C10.15,20 8.45,19.37 7.1,18.31L18.31,7.1C19.37,8.45 20,10.15 20,12C20,16.42 16.42,20 12,20Z"/>
                </svg>
                <span>This contact is blocked. You cannot send or receive messages. Unblock to continue messaging.</span>
              </div>

              <div class="message-input-form">
                <!-- File Upload Button with Dropdown -->
                <div class="attachment-wrapper">
                  <button
                    @click="toggleAttachmentMenu"
                    class="attach-file-btn"
                    :disabled="sendingMessage || (selectedConversation && (selectedConversation.blocked === 1 || selectedConversation.blocked === true))"
                    title="Attach file"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M16.5 6v11.5c0 2.21-1.79 4-4 4s-4-1.79-4-4V5c0-1.38 1.12-2.5 2.5-2.5s2.5 1.12 2.5 2.5v10.5c0 .55-.45 1-1 1s-1-.45-1-1V6H10v9.5c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5V5c0-2.21-1.79-4-4-4S7 2.79 7 5v12.5c0 3.04 2.46 5.5 5.5 5.5s5.5-2.46 5.5-5.5V6h-1.5z"/>
                    </svg>
                  </button>

                  <!-- Attachment Type Dropdown -->
                  <div v-if="showAttachmentMenu" class="attachment-menu">
                    <button @click="selectAttachmentType('image')" class="attachment-menu-item">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
                      </svg>
                      <span>Image</span>
                    </button>
                    <button @click="selectAttachmentType('document')" class="attachment-menu-item">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
                      </svg>
                      <span>Document</span>
                    </button>
                  </div>
                </div>

                <!-- Hidden File Inputs -->
                <input
                  ref="imageInput"
                  type="file"
                  accept="image/*"
                  @change="handleImageSelect"
                  style="display: none;"
                />
                <input
                  ref="documentInput"
                  type="file"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rar"
                  @change="handleDocumentSelect"
                  style="display: none;"
                />

                <!-- Quick Replies Button -->
                <div class="quick-replies-wrapper">
                  <!-- Show "Add Quick Replies" button ONLY for owners when no quick replies exist (and not loading) -->
                  <button
                    v-if="!isAgent && !loadingQuickReplies && quickReplies.length === 0"
                    @click="navigateToQuickReplies"
                    class="add-quick-replies-btn"
                    :disabled="sendingMessage || (selectedConversation && (selectedConversation.blocked === 1 || selectedConversation.blocked === true))"
                    title="Add Quick Replies"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                    </svg>
                  </button>

                  <!-- Show quick replies button for everyone else (agents always, owners when they have replies or loading) -->
                  <button
                    v-else-if="isAgent || loadingQuickReplies || quickReplies.length > 0"
                    @click="toggleQuickReplies"
                    class="quick-replies-btn"
                    :disabled="sendingMessage || (selectedConversation && (selectedConversation.blocked === 1 || selectedConversation.blocked === true))"
                    title="Quick Replies"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 3C6.5 3 2 6.58 2 11c0 2.16.97 4.09 2.5 5.5V21l4.38-2.71C9.87 18.75 10.93 19 12 19c5.5 0 10-3.58 10-8s-4.5-8-10-8m0 14c-.67 0-1.34-.07-2-.21L8 18v-1.93c-1.65-1.31-2.73-3.28-2.73-5.57 0-3.31 3.13-6 7-6s7 2.69 7 6-3.13 6-7 6z"/>
                    </svg>
                    <span v-if="quickReplies.length > 0" class="quick-replies-count">{{ quickReplies.length }}</span>
                  </button>

                  <!-- Quick Replies Dropdown -->
                  <div v-if="showQuickRepliesDropdown" class="quick-replies-dropdown">
                    <div v-if="loadingQuickReplies" class="dropdown-loading">
                      <div class="loading-spinner-small"></div>
                      <span>Loading...</span>
                    </div>
                    <div v-else-if="quickReplies.length === 0" class="dropdown-empty">
                      <p>No quick replies available</p>
                      <router-link v-if="!isAgent" to="/quick-replies" class="create-link">Create Quick Replies</router-link>
                    </div>
                    <div v-else class="quick-replies-list">
                      <div
                        v-for="reply in quickReplies"
                        :key="reply.id"
                        @click="useQuickReply(reply)"
                        class="quick-reply-item"
                      >
                        <img v-if="reply.image_url" :src="reply.image_url" class="reply-thumbnail" alt="" />
                        <div class="reply-text-wrap">
                          <div class="reply-title">{{ reply.title }}</div>
                          <div class="reply-preview">
                            <span v-if="reply.message">{{ reply.message.substring(0, 50) }}{{ reply.message.length > 50 ? '...' : '' }}</span>
                            <span v-else class="reply-image-only">📷 Image</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Image Preview -->
                <div v-if="selectedImage" class="image-preview">
                  <img :src="selectedImage.preview" alt="Selected image" class="preview-img" />
                  <button @click="removeSelectedImage" class="remove-image-btn">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                    </svg>
                  </button>
                </div>

                <!-- Document Preview -->
                <div v-if="selectedDocument" class="document-preview">
                  <div class="document-info">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
                    </svg>
                    <div class="document-details">
                      <span class="document-name">{{ selectedDocument.name }}</span>
                      <span class="document-size">{{ formatFileSize(selectedDocument.size) }}</span>
                    </div>
                  </div>
                  <button @click="removeSelectedDocument" class="remove-document-btn">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                    </svg>
                  </button>
                </div>

                <!-- Reply-to Banner -->
                <div v-if="replyToMessage" class="reply-to-banner">
                  <div class="reply-to-content">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" class="reply-to-icon">
                      <path d="M10 9V5l-7 7 7 7v-4.1c5 0 8.5 1.6 11 5.1-1-5-4-10-11-11z"/>
                    </svg>
                    <div class="reply-to-text">
                      <span class="reply-to-sender">{{ replyToMessage.from }}</span>
                      <span class="reply-to-preview">{{ (replyToMessage.message ? replyToMessage.message.substring(0, 80) + (replyToMessage.message.length > 80 ? '…' : '') : (replyToMessage.image_url ? '📷 Image' : replyToMessage.document_url ? '📄 Document' : replyToMessage.audio_url ? '🎵 Audio' : 'Message')) }}</span>
                    </div>
                  </div>
                  <button @click="clearReply" class="reply-to-cancel" title="Cancel reply">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                    </svg>
                  </button>
                </div>

                <!-- Typing Indicator -->
                <div v-if="typingUsers.length > 0" class="typing-indicator">
                  <span class="typing-text">
                    {{ typingUsers.length === 1 ? `${typingUsers[0].userName} is typing` : `${typingUsers.length} people are typing` }}
                  </span>
                  <span class="typing-dots">
                    <span class="dot"></span>
                    <span class="dot"></span>
                    <span class="dot"></span>
                  </span>
                </div>

                <!-- Message Input -->
                <div class="input-wrapper">
                  <textarea
                    ref="messageTextarea"
                    v-model="newMessage"
                    @keydown.enter.prevent="handleEnterKey"
                    @input="onMessageInput"
                    placeholder="Type a message..."
                    class="message-input"
                    :disabled="sendingMessage || (selectedConversation && (selectedConversation.blocked === 1 || selectedConversation.blocked === true))"
                  ></textarea>
                </div>

                <!-- Send Button -->
                <button
                  @click="sendMessage"
                  class="send-message-btn"
                  :disabled="(!newMessage.trim() && !selectedImage && !selectedDocument) || sendingMessage || (selectedConversation && (selectedConversation.blocked === 1 || selectedConversation.blocked === true))"
                >
                  <div v-if="sendingMessage" class="sending-spinner"></div>
                  <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>

    <!-- Customer Notes Modal -->
    <div v-if="showNotesModal" class="notes-modal-overlay" @click="closeNotesModal">
      <div class="notes-modal" @click.stop>
        <div class="notes-modal-header">
          <h3>Customer Notes</h3>
          <div class="modal-header-info">
            <span class="customer-name">{{ selectedConversation?.from || 'Unknown Contact' }}</span>
            <span class="customer-id">{{ selectedConversation?.wa_id }}</span>
          </div>
          <button @click="closeNotesModal" class="modal-close-btn">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>

        <div class="notes-modal-content">
          <!-- Add New Note -->
          <div class="add-note-form">
            <textarea
              v-model="newNote"
              placeholder="Add a note about this customer..."
              class="note-input"
              rows="3"
            ></textarea>
            <button
              @click="addCustomerNote"
              :disabled="!newNote.trim() || loadingNotes"
              class="add-note-btn"
            >
              <span v-if="loadingNotes">Adding...</span>
              <span v-else>Add Note</span>
            </button>
          </div>

          <!-- Notes List -->
          <div class="notes-list">
            <div v-if="loadingNotes && customerNotes.length === 0" class="notes-loading">
              <div class="loading-spinner"></div>
              <span>Loading notes...</span>
            </div>

            <div v-else-if="customerNotes.length === 0" class="no-notes">
              <div class="no-notes-icon">📝</div>
              <p>No notes for this customer yet</p>
              <small>Add your first note above to get started</small>
            </div>

            <div
              v-for="note in customerNotes"
              :key="note.id"
              class="note-item"
            >
              <div v-if="editingNote === note.id" class="edit-note-form">
                <textarea
                  v-model="editNoteText"
                  class="edit-note-input"
                  rows="3"
                ></textarea>
                <div class="edit-note-actions">
                  <button @click="saveNoteEdit(note)" class="save-btn">Save</button>
                  <button @click="cancelNoteEdit" class="cancel-btn">Cancel</button>
                </div>
              </div>

              <div v-else class="note-display">
                <div class="note-content">
                  <p>{{ note.note }}</p>
                  <div class="note-meta">
                    <span class="note-author">By: {{ note.agent_name || 'Agent' }}</span>
                    <span class="note-date">{{ note.formatted_date || formatDate(note.date_updated) }}</span>
                  </div>
                </div>
                <div class="note-actions">
                  <button @click="startEditNote(note)" class="edit-note-btn" title="Edit note">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                    </svg>
                  </button>
                  <button @click="deleteCustomerNote(note.id)" class="delete-note-btn" title="Delete note">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Chatbot Confirmation Modal -->
    <div v-if="showChatbotConfirmModal" class="modal-overlay" @click="closeChatbotConfirmModal">
      <div class="confirm-modal" @click.stop>
        <div class="confirm-modal-header">
          <div class="warning-icon">⚠️</div>
          <h3>Turn Off Chatbot?</h3>
        </div>
        <div class="confirm-modal-content">
          <p>Are you sure you want to <strong>disable the chatbot</strong> for this customer?</p>
          <p class="customer-info">Customer: <span>{{ selectedConversation?.from }}</span></p>
          <p class="warning-text">The customer will no longer receive automated responses.</p>
        </div>
        <div class="confirm-modal-actions">
          <button @click="closeChatbotConfirmModal" class="cancel-btn">Cancel</button>
          <button @click="confirmTurnOffChatbot" class="confirm-btn" :disabled="loadingChatbot">
            <span v-if="loadingChatbot">Turning Off...</span>
            <span v-else>Yes, Turn Off</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Notification Toast -->
    <div v-if="notification" class="notification-toast" :class="notification.type">
      <div class="notification-icon">
        <svg v-if="notification.type === 'success'" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
        </svg>
        <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M11 15h2v2h-2zm0-8h2v6h-2zm.99-5C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z"/>
        </svg>
      </div>
      <div class="notification-content">
        <div class="notification-title">{{ notification.title }}</div>
        <div class="notification-message">{{ notification.message }}</div>
      </div>
      <button @click="dismissNotification" class="notification-close">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
        </svg>
      </button>
    </div>

    <!-- Analysis Confirmation Modal -->
    <div v-if="showAnalysisConfirmModal" class="modal-overlay" @click="closeAnalysisConfirmModal">
      <div class="confirm-modal" @click.stop>
        <div class="confirm-modal-header">
          <div class="info-icon">🔍</div>
          <h3>Force New Analysis?</h3>
        </div>
        <div class="confirm-modal-content">
          <p><strong>{{ analysisModalData?.message }}</strong></p>
          <p class="analysis-info">Last Analysis: <span>{{ analysisModalData?.lastAnalysisTime }}</span></p>
          <p class="info-text">Do you want to force a new analysis anyway?</p>
        </div>
        <div class="confirm-modal-actions">
          <button @click="cancelAnalysis" class="cancel-btn">No, Use Recent Analysis</button>
          <button @click="confirmForceAnalysis" class="confirm-btn" :disabled="loadingAnalysis">
            <span v-if="loadingAnalysis">Analyzing...</span>
            <span v-else>Yes, Force New Analysis</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Create Lead Modal -->
    <CreateLeadModal
      :show="showCreateLeadModal"
      :phone-number="selectedConversation?.wa_id || ''"
      :contact-name="selectedConversation?.from || ''"
      @close="closeCreateLeadModal"
      @lead-created="onLeadCreated"
    />

    <!-- Media Gallery Side Panel -->
    <div v-if="showMediaGallery" class="media-gallery-overlay" @click="closeMediaGallery">
      <div class="media-gallery-panel" @click.stop>
        <div class="media-gallery-header">
          <h3>Shared Media</h3>
          <button @click="closeMediaGallery" class="close-gallery-btn">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>

        <!-- Tabs for different media types -->
        <div class="media-tabs">
            <button
              @click="activeMediaTab = 'images'"
              class="media-tab"
              :class="{ 'active': activeMediaTab === 'images' }"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M21,3H3C2,3 1,4 1,5V19A2,2 0 0,0 3,21H21C22,21 23,20 23,19V5C23,4 22,3 21,3M5,17L8.5,12.5L11,15.5L14.5,11L19,17H5Z"/>
              </svg>
              <span>Images ({{ mediaGalleryTotals.images }})</span>
            </button>
            <button
              @click="activeMediaTab = 'videos'"
              class="media-tab"
              :class="{ 'active': activeMediaTab === 'videos' }"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17,10.5V7A1,1 0 0,0 16,6H4A1,1 0 0,0 3,7V17A1,1 0 0,0 4,18H16A1,1 0 0,0 17,17V13.5L21,17.5V6.5L17,10.5Z"/>
              </svg>
              <span>Videos ({{ mediaGalleryTotals.videos }})</span>
            </button>
            <button
              @click="activeMediaTab = 'audio'"
              class="media-tab"
              :class="{ 'active': activeMediaTab === 'audio' }"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12,2A3,3 0 0,1 15,5V11A3,3 0 0,1 12,14A3,3 0 0,1 9,11V5A3,3 0 0,1 12,2M19,11C19,14.53 16.39,17.44 13,17.93V21H11V17.93C7.61,17.44 5,14.53 5,11H7A5,5 0 0,0 12,16A5,5 0 0,0 17,11H19Z"/>
              </svg>
              <span>Audio ({{ mediaGalleryTotals.audio }})</span>
            </button>
            <button
              @click="activeMediaTab = 'documents'"
              class="media-tab"
              :class="{ 'active': activeMediaTab === 'documents' }"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2M18 20H6V4H13V9H18V20M10 19L12 15H9V10H13L11 14H14V19H10Z"/>
              </svg>
              <span>Documents ({{ mediaGalleryTotals.documents }})</span>
            </button>
        </div>

        <div v-if="loadingMedia" class="media-gallery-loading">
          <div class="loading-spinner"></div>
          <p>Loading media...</p>
        </div>

        <div v-else class="media-gallery-content">
          <!-- Images Grid -->
          <div v-if="activeMediaTab === 'images'" class="media-grid">
            <div v-if="mediaGalleryData.images.length === 0" class="no-media">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor" opacity="0.3">
                <path d="M21,3H3C2,3 1,4 1,5V19A2,2 0 0,0 3,21H21C22,21 23,20 23,19V5C23,4 22,3 21,3M5,17L8.5,12.5L11,15.5L14.5,11L19,17H5Z"/>
              </svg>
              <p>No images shared</p>
            </div>
            <div
              v-for="media in mediaGalleryData.images"
              :key="media.id"
              class="media-item"
              @click="openImageViewer(media.url)"
            >
              <img :src="media.url" alt="Shared image" />
              <div class="media-item-overlay">
                <button @click.stop="downloadMedia(media.url)" class="media-download-btn" title="Download">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M5,20H19V18H5M19,9H15V3H9V9H5L12,16L19,9Z"/>
                  </svg>
                </button>
              </div>
              <div class="media-item-date">{{ formatMediaDate(media.date_time) }}</div>
            </div>
          </div>

          <!-- Videos Grid -->
          <div v-if="activeMediaTab === 'videos'" class="media-grid">
            <div v-if="mediaGalleryData.videos.length === 0" class="no-media">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor" opacity="0.3">
                <path d="M17,10.5V7A1,1 0 0,0 16,6H4A1,1 0 0,0 3,7V17A1,1 0 0,0 4,18H16A1,1 0 0,0 17,17V13.5L21,17.5V6.5L17,10.5Z"/>
              </svg>
              <p>No videos shared</p>
            </div>
            <div
              v-for="media in mediaGalleryData.videos"
              :key="media.id"
              class="media-item video-item"
            >
              <video :src="media.url" class="media-video-preview"></video>
              <div class="media-item-overlay">
                <button @click="playVideo(media.url)" class="media-play-btn" title="Play">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8,5.14V19.14L19,12.14L8,5.14Z"/>
                  </svg>
                </button>
                <button @click.stop="downloadMedia(media.url)" class="media-download-btn" title="Download">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M5,20H19V18H5M19,9H15V3H9V9H5L12,16L19,9Z"/>
                  </svg>
                </button>
              </div>
              <div class="media-item-date">{{ formatMediaDate(media.date_time) }}</div>
            </div>
          </div>

          <!-- Audio Grid -->
          <div v-if="activeMediaTab === 'audio'" class="media-list">
            <div v-if="mediaGalleryData.audio.length === 0" class="no-media">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor" opacity="0.3">
                <path d="M12,2A3,3 0 0,1 15,5V11A3,3 0 0,1 12,14A3,3 0 0,1 9,11V5A3,3 0 0,1 12,2M19,11C19,14.53 16.39,17.44 13,17.93V21H11V17.93C7.61,17.44 5,14.53 5,11H7A5,5 0 0,0 12,16A5,5 0 0,0 17,11H19Z"/>
              </svg>
              <p>No audio shared</p>
            </div>
            <div
              v-for="media in mediaGalleryData.audio"
              :key="media.id"
              class="media-audio-item"
            >
              <div class="audio-info">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12,2A3,3 0 0,1 15,5V11A3,3 0 0,1 12,14A3,3 0 0,1 9,11V5A3,3 0 0,1 12,2M19,11C19,14.53 16.39,17.44 13,17.93V21H11V17.93C7.61,17.44 5,14.53 5,11H7A5,5 0 0,0 12,16A5,5 0 0,0 17,11H19Z"/>
                </svg>
                <div class="audio-details">
                  <span class="audio-label">Voice Note</span>
                  <span class="audio-date">{{ formatMediaDate(media.date_time) }}</span>
                </div>
              </div>
              <div class="audio-actions">
                <audio :src="media.url" controls class="audio-player"></audio>
                <button @click="downloadMedia(media.url)" class="media-download-btn-small" title="Download">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M5,20H19V18H5M19,9H15V3H9V9H5L12,16L19,9Z"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <!-- Documents List -->
          <div v-if="activeMediaTab === 'documents'" class="media-list">
            <div v-if="mediaGalleryData.documents.length === 0" class="no-media">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor" opacity="0.3">
                <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2M18 20H6V4H13V9H18V20Z"/>
              </svg>
              <p>No documents shared</p>
            </div>
            <div
              v-for="media in mediaGalleryData.documents"
              :key="media.id"
              class="media-document-item"
            >
              <div class="document-info">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2M18 20H6V4H13V9H18V20Z"/>
                </svg>
                <div class="document-details">
                  <span class="document-name">{{ getDocumentName(media.url) }}</span>
                  <span class="document-date">{{ formatMediaDate(media.date_time) }}</span>
                </div>
              </div>
              <div class="document-actions">
                <button @click="openDocument(media.url)" class="document-view-btn" title="View">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12,9A3,3 0 0,0 9,12A3,3 0 0,0 12,15A3,3 0 0,0 15,12A3,3 0 0,0 12,9M12,17A5,5 0 0,1 7,12A5,5 0 0,1 12,7A5,5 0 0,1 17,12A5,5 0 0,1 12,17M12,4.5C7,4.5 2.73,7.61 1,12C2.73,16.39 7,19.5 12,19.5C17,19.5 21.27,16.39 23,12C21.27,7.61 17,4.5 12,4.5Z"/>
                  </svg>
                </button>
                <button @click="downloadMedia(media.url)" class="media-download-btn-small" title="Download">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M5,20H19V18H5M19,9H15V3H9V9H5L12,16L19,9Z"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Quick Send Template Side Panel -->
    <div v-if="showQuickSendTemplatePanel" class="template-panel-overlay" @click="closeQuickSendTemplatePanel">
      <div class="template-panel" @click.stop>
        <div class="template-panel-header">
          <h3>Quick Send Template</h3>
          <button @click="closeQuickSendTemplatePanel" class="close-panel-btn">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>

        <div class="template-panel-content">
          <!-- Loading State -->
          <div v-if="loadingTemplates" class="panel-loading">
            <div class="loading-animation">
              <div class="loading-dots">
                <div class="dot"></div>
                <div class="dot"></div>
                <div class="dot"></div>
              </div>
              <p class="loading-text">Loading templates</p>
            </div>
          </div>

          <!-- Content -->
          <div v-else class="panel-form">
            <!-- Template Selection -->
            <div class="form-section">
              <label class="section-label">Template</label>
              <select v-model="templateForm.selectedTemplateId" @change="onTemplateSelect" class="panel-select">
                <option value="">Select template</option>
                <option v-for="template in approvedTemplatesForPanel" :key="template.id" :value="template.id">
                  {{ template.name }}
                </option>
              </select>
            </div>

            <!-- Template Preview & Variables -->
            <div v-if="templateForm.selectedTemplate" class="template-preview-section">
              <!-- Simplified Preview -->
              <div class="preview-header">
                <span class="preview-label">Preview</span>
                <span class="recipient-phone">{{ selectedConversation?.wa_id }}</span>
              </div>
              <div class="simple-preview-bubble">
                <div class="bubble-content">
                      <!-- Header Component -->
                      <div v-if="getTemplateHeaderComponent()" class="message-header-preview">
                        <div v-if="getTemplateHeaderComponent().format === 'TEXT'" class="header-text-preview">
                          {{ getTemplateHeaderComponent().text }}
                        </div>
                        <div v-else-if="getTemplateHeaderComponent().format === 'IMAGE'" class="header-media-preview">
                          <img
                            v-if="templateForm.mediaUrl"
                            :src="templateForm.mediaUrl"
                            alt="Template image"
                            class="preview-image"
                          />
                          <div v-else class="media-placeholder">📷 Image</div>
                        </div>
                        <div v-else-if="getTemplateHeaderComponent().format === 'VIDEO'" class="header-media-preview">
                          <div class="media-placeholder">🎥 Video</div>
                        </div>
                        <div v-else-if="getTemplateHeaderComponent().format === 'DOCUMENT'" class="header-media-preview">
                          <div class="media-placeholder">📄 Document</div>
                        </div>
                      </div>

                      <!-- Body Component -->
                      <div v-if="getTemplateBodyComponent()" class="message-body-preview">
                        <p v-html="replaceTemplateVariables(getTemplateBodyComponent().text)"></p>
                      </div>

                      <!-- Footer Component -->
                      <div v-if="getTemplateFooterComponent()" class="message-footer-preview">
                        {{ getTemplateFooterComponent().text }}
                      </div>

                      <!-- Buttons Component -->
                      <div v-if="getTemplateButtonsComponent()" class="message-buttons-preview">
                        <button
                          v-for="(button, idx) in getTemplateButtonsComponent().buttons"
                          :key="idx"
                          class="preview-button"
                        >
                          {{ button.text }}
                        </button>
                      </div>
                </div>
              </div>

              <!-- Media URL Input (for IMAGE/VIDEO/DOCUMENT headers) -->
              <div v-if="hasMediaHeader()" class="form-section">
                <label class="section-label">{{ getMediaHeaderType() }} URL *</label>
                <input
                  v-model="templateForm.mediaUrl"
                  type="url"
                  :placeholder="`Enter ${getMediaHeaderType().toLowerCase()} URL`"
                  class="panel-input"
                />
              </div>

              <!-- Variable Mapping -->
              <div v-if="getTemplateVariables().length > 0" class="form-section">
                <label class="section-label">Template Variables</label>
                <div class="variables-list">
                  <div v-for="varIndex in getTemplateVariables()" :key="varIndex" class="variable-card">
                    <div class="variable-header">
                      <span class="variable-badge">{{ formatVariableBadge(varIndex) }}</span>

                      <!-- Source Type Selector -->
                      <div class="variable-type-selector">
                        <label class="type-radio">
                          <input
                            type="radio"
                            :name="`var-${varIndex}`"
                            value="customer_field"
                            v-model="getOrCreateVariableMapping(varIndex).source_type"
                          />
                          <span>Customer Field</span>
                        </label>
                        <label class="type-radio">
                          <input
                            type="radio"
                            :name="`var-${varIndex}`"
                            value="static_value"
                            v-model="getOrCreateVariableMapping(varIndex).source_type"
                          />
                          <span>Static Value</span>
                        </label>
                      </div>
                    </div>

                    <!-- Customer Field Option -->
                    <div v-if="getOrCreateVariableMapping(varIndex).source_type === 'customer_field'" class="variable-inputs">
                      <select v-model="getOrCreateVariableMapping(varIndex).customer_field" class="panel-select-sm">
                        <option value="">Select field...</option>
                        <option value="full_name">Customer Name</option>
                        <option value="phone_number">Phone Number</option>
                        <option value="email">Email</option>
                        <option value="city">City</option>
                        <option value="country">Country</option>
                      </select>
                      <input
                        v-model="getOrCreateVariableMapping(varIndex).default_value"
                        type="text"
                        placeholder="Fallback value..."
                        class="panel-input-sm"
                      />
                    </div>

                    <!-- Static Value Option -->
                    <div v-if="getOrCreateVariableMapping(varIndex).source_type === 'static_value'" class="variable-inputs">
                      <input
                        v-model="getOrCreateVariableMapping(varIndex).static_value"
                        type="text"
                        placeholder="Enter static value..."
                        class="panel-input"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Error Message -->
              <div v-if="templateError" class="error-banner">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M13,14H11V10H13M13,18H11V16H13M1,21H23L12,2L1,21Z"/>
                </svg>
                <span>{{ templateError }}</span>
              </div>

              <!-- Success Message -->
              <div v-if="templateSuccess" class="success-banner">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"/>
                </svg>
                <span>{{ templateSuccess }}</span>
              </div>

              <!-- Send Button -->
              <button
                @click="sendQuickTemplate"
                class="send-template-btn"
                :disabled="!canSendTemplate || sendingTemplate"
              >
                <div v-if="sendingTemplate" class="btn-spinner"></div>
                <span v-else>Send Template</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Image Viewer Modal -->
    <div v-if="showImageViewer" class="image-viewer-modal" @click="closeImageViewer">
      <div class="image-viewer-content" @click.stop>
        <!-- Top Controls Bar -->
        <div class="image-viewer-controls">
          <div class="controls-left">
            <span class="image-label">Image Preview</span>
          </div>
          <div class="controls-right">
            <a :href="viewerImageUrl" target="_blank" rel="noopener noreferrer" class="image-viewer-btn" title="Open in new tab">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z"/>
              </svg>
            </a>
            <button class="image-viewer-btn" @click="closeImageViewer" title="Close (Esc)">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
            </button>
          </div>
        </div>
        <!-- Image Container -->
        <div class="image-container">
          <img :src="viewerImageUrl" alt="Full size image" class="viewer-image" @click.stop />
        </div>
      </div>
    </div>

    <!-- Forward Message Modal -->
    <div v-if="showForwardModal" class="forward-modal" @click="closeForwardModal">
      <div class="forward-modal-content" @click.stop>
        <!-- Header -->
        <div class="forward-modal-header">
          <h3>Forward Message</h3>
          <button @click="closeForwardModal" class="modal-close-btn">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>

        <!-- Message Preview -->
        <div class="forward-message-preview">
          <div class="preview-label">Message to forward:</div>
          <div class="preview-content">
            <div v-if="messageToForward?.image_url" class="preview-image">
              <img :src="messageToForward.image_url" alt="Preview" />
            </div>
            <div v-if="messageToForward?.document_url" class="preview-document">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
              </svg>
              <span>{{ getDocumentName(messageToForward.document_url) }}</span>
            </div>
            <p v-if="messageToForward?.message" class="preview-text">{{ messageToForward.message }}</p>
          </div>
        </div>

        <!-- Search -->
        <div class="forward-search">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
          </svg>
          <input
            v-model="forwardSearchQuery"
            type="text"
            placeholder="Search conversations..."
            class="forward-search-input"
          />
        </div>

        <!-- Conversations List -->
        <div class="forward-conversations-list">
          <div
            v-for="conv in filteredForwardConversations"
            :key="conv.wa_id"
            @click="selectForwardConversation(conv)"
            class="forward-conversation-item"
            :class="{ 'selected': selectedForwardConv?.wa_id === conv.wa_id, 'current': conv.wa_id === selectedConversation?.wa_id }"
          >
            <div class="forward-conv-avatar">
              {{ conv.from.charAt(0).toUpperCase() }}
            </div>
            <div class="forward-conv-info">
              <div class="forward-conv-name">{{ conv.from }}</div>
              <div class="forward-conv-number">{{ conv.wa_id }}</div>
            </div>
            <div v-if="conv.wa_id === selectedConversation?.wa_id" class="current-badge">Current</div>
            <div v-if="selectedForwardConv?.wa_id === conv.wa_id" class="selected-check">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
              </svg>
            </div>
          </div>
          <div v-if="filteredForwardConversations.length === 0" class="no-conversations">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor" opacity="0.3">
              <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>
            </svg>
            <p>No conversations found</p>
          </div>
        </div>

        <!-- Footer Actions -->
        <div class="forward-modal-footer">
          <button @click="closeForwardModal" class="btn-cancel">Cancel</button>
          <button
            @click="confirmForward"
            :disabled="!selectedForwardConv || forwarding || selectedForwardConv?.wa_id === selectedConversation?.wa_id"
            class="btn-forward"
          >
            <span v-if="forwarding">Forwarding...</span>
            <span v-else>Forward</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Sidebar from '../components/Sidebar.vue'
import TopHeader from '../components/TopHeader.vue'
import CreateLeadModal from '../components/CreateLeadModal.vue'
import { DashboardService } from '../services/dashboard'
import { AuthService } from '../services/auth'
import { WhatsAppService } from '../services/whatsapp'
import { requestNotificationPermission, onMessageListener, saveFCMTokenToBackend } from '../services/firebase'

export default {
  name: 'WhatsAppView',
  components: {
    Sidebar,
    TopHeader,
    CreateLeadModal
  },
  data () {
    return {
      isAgent: !!localStorage.getItem('agent_token'),
      conversations: [],
      loading: false,
      searchQuery: '',
      searchTimeout: null,
      currentPage: 1,
      hasMoreConversations: true,
      loadingMore: false,
      selectedImageUrl: null,
      filters: {
        archived: 0,
        needs_agent: '',
        agent_id: null,
        is_agent: false,
        department_id: '',
        unread_only: 0
      },
      departmentModeEnabled: false,
      departments: [],
      refreshInterval: null,
      messagesRefreshInterval: null,
      selectedConversation: null,
      conversationMessages: [],
      loadingMessages: false,
      newMessage: '',
      replyToMessage: null,
      selectedImage: null,
      selectedDocument: null,
      attachmentType: 'image', // 'image' or 'document'
      showAttachmentMenu: false,
      sendingMessage: false,
      customerNotes: [],
      loadingNotes: false,
      newNote: '',
      editingNote: null,
      editNoteText: '',
      showNotesModal: false,
      notesCount: null,
      chatbotStatus: null,
      loadingChatbot: false,
      loadingAnalysis: false,
      loadingBlock: false,
      loadingMarkUnread: false,
      showChatbotConfirmModal: false,
      contextMenu: {
        show: false,
        x: 0,
        y: 0,
        conversation: null
      },
      showAnalysisConfirmModal: false,
      analysisModalData: null,
      notification: null,
      showCreateLeadModal: false,
      showQuickSendTemplatePanel: false,
      conversationHasLead: false,
      leadIsComplete: false,
      // Quick Send Template Panel
      loadingTemplates: false,
      approvedTemplatesForPanel: [],
      templateForm: {
        selectedTemplateId: '',
        selectedTemplate: null,
        mediaUrl: '',
        variableMappings: []
      },
      sendingTemplate: false,
      templateError: '',
      templateSuccess: '',
      allowAgentSendTemplate: false, // Permission for agents to send templates
      showImageViewer: false,
      viewerImageUrl: null,
      showForwardModal: false,
      messageToForward: null,
      selectedForwardConv: null,
      forwardSearchQuery: '',
      forwarding: false,
      hasWhatsAppCredentials: null, // null = checking, true = has credentials, false = no credentials
      playingAudioId: null,
      audioDurations: {},
      audioCurrentTimes: {},
      // Notification sound properties
      notificationSound: null,
      notificationEnabled: true,
      previousMessageCount: 0,
      audioUnlocked: false, // Track if user has interacted with the page
      // FCM Web Push Notifications
      fcmToken: null,
      fcmInitialized: false,
      // Quick Replies
      quickReplies: [],
      loadingQuickReplies: false,
      showQuickRepliesDropdown: false,
      // Live Viewers WebSocket
      viewersWs: null,
      liveViewers: [],
      heartbeatInterval: null,
      pendingConversationJoin: null,

      // Typing Indicator
      typingUsers: [], // Array of {user_id, user_name, user_role}
      typingTimeout: null,

      // Department Transfer
      showTransferModal: false,
      transferData: {
        customerPhone: '',
        customerName: '',
        currentDepartmentId: null,
        currentDepartmentName: '',
        newDepartmentId: '',
        language: 'ar'
      },
      allDepartments: [],
      transferLoading: false,
      transferError: null,

      // Media Gallery
      showMediaGallery: false,
      loadingMedia: false,
      activeMediaTab: 'images',
      mediaGalleryData: {
        images: [],
        videos: [],
        audio: [],
        documents: []
      },
      mediaGalleryTotals: {
        images: 0,
        videos: 0,
        audio: 0,
        documents: 0,
        total: 0
      }
    }
  },
  async mounted () {
    // Initialize notification sound
    this.initializeNotificationSound()

    // Add user interaction listener to unlock audio
    this.setupAudioUnlock()

    // Initialize FCM Web Push Notifications (fire and forget - must not block page load)
    this.initializeFCMNotifications()

    // Add click outside listener for quick replies dropdown
    document.addEventListener('click', this.handleClickOutside)

    // Load quick replies on mount
    await this.loadQuickReplies()

    await this.checkWhatsAppCredentials()
    if (this.hasWhatsAppCredentials !== false) {
      // Check if department mode is enabled (for both owners and agents)
      await this.checkDepartmentMode()
      await this.loadConversations()
      this.startAutoRefresh()

      // Load agent template permission if user is an agent
      if (this.isAgent) {
        await this.loadAgentTemplatePermission()
      }

      // Initialize live viewers WebSocket
      this.connectViewersWebSocket()
    }
  },
  beforeUnmount () {
    this.stopAutoRefresh()
    this.stopMessagesAutoRefresh()
    this.removeAudioUnlock()
    this.disconnectViewersWebSocket()
    document.removeEventListener('click', this.handleClickOutside)
  },
  computed: {
    messageWaIdMap () {
      const map = {}
      for (const msg of this.conversationMessages) {
        if (msg.wa_message_id) {
          map[msg.wa_message_id] = msg
        }
      }
      return map
    },

    canSendTemplates () {
      // Regular users can always send templates
      if (!this.isAgent) return true
      // Agents can send templates only if permission is enabled
      return this.allowAgentSendTemplate
    },
    filteredForwardConversations () {
      if (!this.forwardSearchQuery.trim()) {
        return this.conversations
      }
      const query = this.forwardSearchQuery.toLowerCase()
      return this.conversations.filter(conv =>
        conv.from.toLowerCase().includes(query) ||
        conv.wa_id.includes(query)
      )
    },
    canSendTemplate () {
      if (!this.templateForm.selectedTemplate) return false
      // Check if media URL is required and provided
      if (this.hasMediaHeader() && !this.templateForm.mediaUrl) return false
      // Check if all variables are mapped
      const variables = this.getTemplateVariables()
      if (variables.length > 0) {
        return variables.every(varIndex => {
          const mapping = this.templateForm.variableMappings.find(m => m.variable_index === varIndex)
          if (!mapping) return false
          if (mapping.source_type === 'static_value') {
            return mapping.static_value && mapping.static_value.trim().length > 0
          } else if (mapping.source_type === 'customer_field') {
            return mapping.customer_field && mapping.customer_field.length > 0
          }
          return false
        })
      }
      return true
    }
  },

  watch: {
    newMessage (val) {
      if (!val) {
        this.$nextTick(() => {
          const el = this.$refs.messageTextarea
          if (el) el.style.height = ''
        })
      }
    }
  },

  methods: {
    getAgentId () {
      // Check if logged in as agent
      const agentData = localStorage.getItem('agent_data')
      if (agentData) {
        try {
          const parsedData = JSON.parse(agentData)
          return parsedData.id || null
        } catch (e) {
          console.error('Error parsing agent_data:', e)
          return null
        }
      }
      return null
    },

    getSenderName () {
      // Check if logged in as agent
      const agentData = localStorage.getItem('agent_data')
      if (agentData) {
        try {
          const parsedData = JSON.parse(agentData)
          return parsedData.name || 'Agent'
        } catch (e) {
          console.error('Error parsing agent_data:', e)
        }
      }

      // Otherwise use regular user data
      const userData = localStorage.getItem('user_data') || localStorage.getItem('user')
      if (userData) {
        try {
          const parsedData = JSON.parse(userData)
          return parsedData.name || parsedData.company || 'User'
        } catch (e) {
          console.error('Error parsing user_data:', e)
        }
      }

      return 'Support'
    },

    // Utility Methods
    getCurrentLocalDateTime () {
      // Get current date/time in user's local timezone
      // Returns format: YYYY-MM-DD HH:MM:SS
      const now = new Date()
      const year = now.getFullYear()
      const month = String(now.getMonth() + 1).padStart(2, '0')
      const day = String(now.getDate()).padStart(2, '0')
      const hours = String(now.getHours()).padStart(2, '0')
      const minutes = String(now.getMinutes()).padStart(2, '0')
      const seconds = String(now.getSeconds()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
    },

    // Notification Sound Methods
    initializeNotificationSound () {
      // Use hosted notification sound
      this.notificationSound = new Audio('https://dashboard.quali-d.com/sounds/notification.mp3')
      this.notificationSound.volume = 0.5 // Set volume to 50%

      // Preload the audio
      this.notificationSound.load()

      console.log('Notification sound initialized:', this.notificationSound)

      // Load notification preference from localStorage
      const savedPreference = localStorage.getItem('whatsapp_notifications_enabled')
      if (savedPreference !== null) {
        this.notificationEnabled = savedPreference === 'true'
      }

      console.log('Notification enabled:', this.notificationEnabled)

      // Request browser notification permission if not already granted
      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission()
      }
    },

    setupAudioUnlock () {
      // Create a handler to unlock audio on first user interaction
      this.audioUnlockHandler = () => {
        if (!this.audioUnlocked && this.notificationSound) {
          console.log('🔓 Unlocking audio after user interaction...')
          // Play and immediately pause to unlock audio context
          this.notificationSound.volume = 0
          this.notificationSound.play()
            .then(() => {
              this.notificationSound.pause()
              this.notificationSound.currentTime = 0
              this.notificationSound.volume = 0.5
              this.audioUnlocked = true
              console.log('✅ Audio unlocked successfully!')
            })
            .catch(err => {
              console.log('Audio unlock attempt:', err.message)
            })
        }
      }

      // Listen for various user interaction events
      document.addEventListener('click', this.audioUnlockHandler, { once: true })
      document.addEventListener('touchstart', this.audioUnlockHandler, { once: true })
      document.addEventListener('keydown', this.audioUnlockHandler, { once: true })
    },

    removeAudioUnlock () {
      if (this.audioUnlockHandler) {
        document.removeEventListener('click', this.audioUnlockHandler)
        document.removeEventListener('touchstart', this.audioUnlockHandler)
        document.removeEventListener('keydown', this.audioUnlockHandler)
      }
    },

    playNotificationSound () {
      console.log('playNotificationSound called - enabled:', this.notificationEnabled, 'unlocked:', this.audioUnlocked, 'sound:', this.notificationSound)

      if (!this.audioUnlocked) {
        console.log('⚠️ Audio not unlocked yet. User needs to interact with the page first.')
        return
      }

      if (this.notificationEnabled && this.notificationSound) {
        try {
          // Reset audio to start
          this.notificationSound.currentTime = 0
          console.log('Attempting to play sound...')

          this.notificationSound.play()
            .then(() => {
              console.log('✅ Notification sound played successfully!')
            })
            .catch(error => {
              console.error('❌ Error playing notification sound:', error.message)
              // If still blocked, audio might need to be unlocked again
              if (error.name === 'NotAllowedError') {
                console.log('⚠️ Audio blocked by browser. Resetting unlock status.')
                this.audioUnlocked = false
              }
            })
        } catch (error) {
          console.error('❌ Exception while playing notification sound:', error)
        }
      } else {
        console.log('Sound not played - enabled:', this.notificationEnabled, 'sound exists:', !!this.notificationSound)
      }
    },

    async toggleNotifications () {
      this.notificationEnabled = !this.notificationEnabled
      localStorage.setItem('whatsapp_notifications_enabled', this.notificationEnabled.toString())

      // Play a test sound when enabling (this also unlocks audio via user interaction)
      if (this.notificationEnabled) {
        // If audio not unlocked, unlock it now (user just clicked)
        if (!this.audioUnlocked && this.notificationSound) {
          console.log('🔓 Unlocking audio via toggle button click...')
          this.notificationSound.volume = 0
          this.notificationSound.play()
            .then(() => {
              this.notificationSound.pause()
              this.notificationSound.currentTime = 0
              this.notificationSound.volume = 0.5
              this.audioUnlocked = true
              console.log('✅ Audio unlocked! Playing test sound...')
              // Now play the test sound
              this.playNotificationSound()
            })
            .catch(err => {
              console.log('Failed to unlock audio:', err.message)
            })
        } else {
          // Already unlocked, just play
          this.playNotificationSound()
        }

        // Request FCM web push notification permission (first-time setup)
        if (this.fcmInitialized && !this.fcmToken && Notification.permission !== 'granted') {
          await this.requestFCMToken(true) // true = show success notification
        }
      }
    },

    // FCM Web Push Notification Methods
    async initializeFCMNotifications () {
      try {
        console.log('🔔 Initializing FCM Web Push Notifications...')

        // Check if notifications are supported
        if (!('Notification' in window)) {
          console.log('⚠️ This browser does not support notifications')
          return
        }

        // Check if service worker is supported
        if (!('serviceWorker' in navigator)) {
          console.log('⚠️ This browser does not support service workers')
          return
        }

        // Register service worker for Firebase Messaging
        const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js')
        console.log('✅ Service Worker registered:', registration)

        // Set up foreground message listener
        onMessageListener((payload) => {
          console.log('📨 Foreground message received:', payload)

          // Play notification sound
          this.playNotificationSound()

          // Show in-app notification
          this.showNotification({
            type: 'success',
            title: payload.notification?.title || 'New Message',
            message: payload.notification?.body || 'You have a new WhatsApp message'
          })

          // Refresh conversations if we're on the WhatsApp page
          if (!document.hidden) {
            this.loadConversations(false, true)
            if (this.selectedConversation) {
              this.loadMessages()
            }
          }
        })

        // Check if notification permission was previously granted
        if (Notification.permission === 'granted') {
          console.log('✅ Notification permission already granted')
          await this.requestFCMToken()
        } else if (Notification.permission === 'default') {
          console.log('ℹ️ Notification permission not yet requested')
          // We'll request permission when user clicks the notification toggle
        } else {
          console.log('❌ Notification permission denied')
        }

        this.fcmInitialized = true
      } catch (error) {
        console.error('❌ Error initializing FCM notifications:', error)
      }
    },

    async requestFCMToken (isFirstTimeSetup = false) {
      try {
        console.log('🔑 Requesting FCM token...')

        const token = await requestNotificationPermission()

        if (token) {
          console.log('✅ FCM token obtained:', token.substring(0, 20) + '...')
          this.fcmToken = token

          // Save token to backend
          const authToken = this.isAgent
            ? localStorage.getItem('agent_token')
            : AuthService.getAuthToken()

          const saved = await saveFCMTokenToBackend(token, authToken)

          if (saved) {
            console.log('✅ FCM token saved to backend')

            // Only show notification on first-time setup (when user clicks the bell)
            // Not on every page load
            if (isFirstTimeSetup) {
              this.showNotification({
                type: 'success',
                title: 'Notifications Enabled',
                message: 'You will now receive web push notifications for new messages'
              })
            }
          } else {
            console.error('❌ Failed to save FCM token to backend')
          }
        } else {
          console.log('❌ Failed to obtain FCM token')
        }
      } catch (error) {
        console.error('❌ Error requesting FCM token:', error)
      }
    },

    async loadConversations (resetPage = false, isBackgroundRefresh = false) {
      try {
        if (resetPage) {
          this.currentPage = 1
          this.conversations = []
          this.hasMoreConversations = true
        }

        // Background refresh always fetches page 1 for notification detection
        const pageToFetch = isBackgroundRefresh ? 1 : this.currentPage

        if (!isBackgroundRefresh) {
          if (pageToFetch === 1) {
            this.loading = true
          } else {
            this.loadingMore = true
          }
        }

        const params = {
          page: pageToFetch,
          search: this.searchQuery,
          ...this.filters
        }

        // Remove empty values
        Object.keys(params).forEach(key => {
          if (params[key] === '' || params[key] === null) {
            delete params[key]
          }
        })

        const result = await DashboardService.getWhatsAppConversations(params)

        if (result.success) {
          const newConversations = result.messages

          if (isBackgroundRefresh) {
            // Detect new messages for notifications
            if (this.conversations.length > 0) {
              console.log('🔍 Checking conversations list for new messages...')

              const oldConversationMap = new Map()
              this.conversations.forEach(conv => {
                oldConversationMap.set(conv.wa_id, {
                  lastMessageTime: conv.date_time,
                  messageId: conv.id
                })
              })

              let hasNewIncomingMessage = false
              newConversations.forEach(conv => {
                const oldData = oldConversationMap.get(conv.wa_id)
                if (oldData) {
                  if (conv.id !== oldData.messageId) {
                    console.log('📨 New message detected in conversation:', {
                      wa_id: conv.wa_id,
                      from: conv.from,
                      oldMessageId: oldData.messageId,
                      newMessageId: conv.id,
                      message: conv.message,
                      conversation_role: conv.conversation_role,
                      read: conv.read
                    })

                    const isUnread = conv.read === 0 || conv.read === false || !conv.read
                    const isFromCustomer = conv.conversation_role === 'Customer'

                    if (isUnread && isFromCustomer) {
                      hasNewIncomingMessage = true
                      console.log('✅ This is an unread customer message!')
                    } else if (isUnread && !isFromCustomer) {
                      console.log('⚠️ Unread but sent by us (Agent/Owner) - no notification')
                    }
                  }
                }
              })

              if (hasNewIncomingMessage) {
                console.log('🔔 New incoming message detected - playing notification!')
                this.playNotificationSound()

                if (document.hidden && 'Notification' in window && Notification.permission === 'granted') {
                  // eslint-disable-next-line no-new
                  new Notification('QUALI-D | Whatsapp Connect ⭐', {
                    body: 'You have a new message',
                    icon: 'https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg',
                    tag: 'whatsapp-message-list'
                  })
                }
              }
            }

            // Smart merge: prepend new conversations + update existing ones
            const freshMap = new Map(newConversations.map(c => [c.wa_id, c]))
            const existingWaIds = new Set(this.conversations.map(c => c.wa_id))
            const brandNew = newConversations.filter(c => !existingWaIds.has(c.wa_id))
            this.conversations = [
              ...brandNew,
              ...this.conversations.map(c => freshMap.get(c.wa_id) || c)
            ]
          } else {
            // Normal load: replace (page 1) or append (load more)
            if (pageToFetch === 1) {
              this.conversations = newConversations
            } else {
              this.conversations = [...this.conversations, ...newConversations]
            }
            this.hasMoreConversations = pageToFetch < result.lastPage

            if (newConversations && newConversations.length > 0 && pageToFetch === 1) {
              console.log('Sample conversation data:', newConversations[0])
            }
          }
        } else {
          console.error('Failed to load WhatsApp conversations:', result.error)
          if (!isBackgroundRefresh) {
            this.conversations = []
            this.hasMoreConversations = false
          }
        }
      } catch (error) {
        console.error('Error loading WhatsApp conversations:', error)
        if (!isBackgroundRefresh) {
          this.conversations = []
          this.hasMoreConversations = false
        }
      } finally {
        if (!isBackgroundRefresh) {
          this.loading = false
          this.loadingMore = false
        }
      }
    },

    handleSearch () {
      // Debounce search
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.loadConversations(true)
      }, 500)
    },

    async selectConversation (conversation) {
      console.log('Selected WhatsApp conversation:', conversation)
      if (!conversation.wa_id) {
        console.error('No WhatsApp ID found in conversation')
        return
      }

      // Stop previous messages auto refresh if any
      this.stopMessagesAutoRefresh()

      // Open conversation detail panel
      this.selectedConversation = conversation
      this.conversationMessages = []
      this.customerNotes = []
      this.loadingMessages = true

      // Load conversation messages
      await this.loadConversationMessages(conversation.wa_id)

      // Load customer notes count (lightweight)
      await this.loadCustomerNotesCount(conversation.wa_id)

      // Load chatbot status
      await this.loadChatbotStatus(conversation.wa_id)

      // Check if lead exists for this conversation
      await this.checkIfLeadExists(conversation.wa_id)

      // Join live viewers for this conversation
      this.joinConversationViewers(conversation.wa_id)

      // Start auto refresh for this conversation's messages
      this.startMessagesAutoRefresh(conversation.wa_id)
    },

    async loadConversationMessages (waId, isBackgroundRefresh = false) {
      try {
        // Only show loading spinner if it's not a background refresh
        if (!isBackgroundRefresh) {
          this.loadingMessages = true
        }

        const result = await DashboardService.getWhatsAppConversationMessages(waId)

        if (result.success) {
          const newMessages = (result.messages || []).reverse()
          const shouldScroll = !isBackgroundRefresh || this.isScrolledToBottom()

          // Detect new incoming messages and play notification sound
          if (isBackgroundRefresh && this.conversationMessages.length > 0) {
            const hasNewMessages = newMessages.length > this.conversationMessages.length
            console.log('🔍 Message detection:', {
              isBackgroundRefresh,
              oldCount: this.conversationMessages.length,
              newCount: newMessages.length,
              hasNewMessages
            })

            if (hasNewMessages) {
              // Find the new messages (messages that are not in the old array)
              const oldMessageIds = new Set(this.conversationMessages.map(m => m.id))
              const newIncomingMessages = newMessages.filter(m => !oldMessageIds.has(m.id))

              console.log('📨 New messages found:', newIncomingMessages.length)
              console.log('New messages:', newIncomingMessages)

              // Only play sound if there are new messages from the customer (not sent by us)
              const hasNewCustomerMessages = newIncomingMessages.some(msg => {
                const isCustomerMessage = msg.conversation_role === 'Customer'
                console.log('Message check:', {
                  id: msg.id,
                  conversation_role: msg.conversation_role,
                  type: msg.type,
                  isCustomerMessage
                })
                return isCustomerMessage
              })

              console.log('🎵 Has new customer messages?', hasNewCustomerMessages)

              if (hasNewCustomerMessages) {
                console.log('🔔 Playing notification sound!')
                this.playNotificationSound()

                // Optional: Show browser notification if page is not focused
                if (document.hidden && 'Notification' in window && Notification.permission === 'granted') {
                  // eslint-disable-next-line no-new
                  new Notification('QUALI-D | Whatsapp Connect ⭐', {
                    body: `New message from ${this.selectedConversation.from}`,
                    icon: 'https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg',
                    tag: 'whatsapp-message'
                  })
                }
              }
            }
          }

          this.conversationMessages = newMessages

          // Auto scroll to bottom after messages load
          if (shouldScroll) {
            this.$nextTick(() => {
              setTimeout(() => {
                this.scrollToBottom(!isBackgroundRefresh) // Force immediate scroll for initial load
              }, isBackgroundRefresh ? 100 : 50)
            })
          }
        } else {
          console.error('Failed to load conversation messages:', result.error)
          if (!isBackgroundRefresh) {
            this.conversationMessages = []
          }
        }
      } catch (error) {
        console.error('Error loading conversation messages:', error)
        if (!isBackgroundRefresh) {
          this.conversationMessages = []
        }
      } finally {
        // Only hide loading spinner if it's not a background refresh
        if (!isBackgroundRefresh) {
          this.loadingMessages = false
        }
      }
    },

    isScrolledToBottom () {
      const messagesContainer = this.$el?.querySelector('.conversation-messages-container')
      if (!messagesContainer) return true

      const threshold = 100 // pixels from bottom
      return messagesContainer.scrollTop + messagesContainer.clientHeight >= messagesContainer.scrollHeight - threshold
    },

    scrollToBottom (force = false) {
      const messagesContainer = this.$el.querySelector('.conversation-messages-container')
      if (messagesContainer) {
        const scrollToEnd = () => {
          // Force scroll to absolute bottom
          messagesContainer.scrollTop = messagesContainer.scrollHeight

          // Double-check after a short delay to ensure we're truly at the bottom
          setTimeout(() => {
            if (messagesContainer.scrollTop + messagesContainer.clientHeight < messagesContainer.scrollHeight - 5) {
              messagesContainer.scrollTop = messagesContainer.scrollHeight
            }
          }, 50)
        }

        if (force) {
          // Immediate scroll for forced updates
          scrollToEnd()
        } else {
          // Use smooth scrolling with multiple timing attempts
          setTimeout(() => {
            messagesContainer.scrollTo({
              top: messagesContainer.scrollHeight,
              behavior: 'smooth'
            })

            // Fallback: ensure we're at the bottom after smooth scroll
            setTimeout(scrollToEnd, 300)
          }, 100)
        }
      }
    },

    closeConversation () {
      // Leave live viewers before closing
      if (this.selectedConversation) {
        this.leaveConversationViewers(this.selectedConversation.wa_id)
      }

      this.stopMessagesAutoRefresh()
      this.selectedConversation = null
      this.conversationMessages = []
      this.customerNotes = []
      this.notesCount = null
      this.newNote = ''
      this.editingNote = null
      this.showNotesModal = false
      this.chatbotStatus = null
    },

    toggleArchived () {
      this.filters.archived = this.filters.archived === 1 ? 0 : 1
      this.loadConversations(true) // Reset page when toggling
    },

    toggleUnread () {
      this.filters.unread_only = this.filters.unread_only === 1 ? 0 : 1
      this.loadConversations(true) // Reset page when toggling
    },

    async loadMoreConversations () {
      if (this.loadingMore || !this.hasMoreConversations) return
      this.currentPage++
      await this.loadConversations()
    },

    handleConversationsScroll (event) {
      const el = event.target
      const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 150
      if (nearBottom) {
        this.loadMoreConversations()
      }
    },

    // ===== Live Viewers WebSocket Methods =====
    connectViewersWebSocket () {
      try {
        // Get authentication token
        const token = localStorage.getItem('auth_token')
        if (!token) {
          console.error('[VIEWERS] No authentication token found')
          return
        }

        // Connect to WebSocket endpoint - send token in subprotocol header
        const wsUrl = 'wss://qualidagents.dvps.online/ws'
        console.log('[VIEWERS] Connecting to WebSocket with authentication...')

        // Send token as subprotocol (Nginx won't strip this)
        this.viewersWs = new WebSocket(wsUrl, ['auth', token])

        this.viewersWs.onopen = () => {
          console.log('[VIEWERS] Connected to live viewers server')
        }

        this.viewersWs.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data)
            console.log('[VIEWERS] Received message:', data)

            switch (data.type) {
              case 'auth_error':
                console.error('[VIEWERS] Authentication error:', data.message)
                this.disconnectViewersWebSocket()
                break

              case 'connected':
                console.log('[VIEWERS]', data.message)
                break

              case 'viewers_update': {
                // Update viewers list, excluding current user
                let currentUserId
                if (this.isAgent) {
                  const agentData = JSON.parse(localStorage.getItem('agent_data') || '{}')
                  currentUserId = agentData.id
                } else {
                  const userData = JSON.parse(localStorage.getItem('user_data') || '{}')
                  currentUserId = userData.id
                }

                this.liveViewers = data.viewers.filter(v => v.id !== currentUserId)
                console.log('[VIEWERS] Updated:', this.liveViewers.length, 'viewers', data.viewers)
                break
              }

              case 'join_success':
                console.log('[VIEWERS] Joined conversation:', data.conversation_id)
                break

              case 'leave_success':
                console.log('[VIEWERS] Left conversation:', data.conversation_id)
                this.liveViewers = []
                break

              case 'heartbeat_ack':
                // Silent
                break

              case 'user_typing':
                this.handleUserTypingEvent(data)
                break

              default:
                console.log('[VIEWERS] Unknown message:', data)
            }
          } catch (error) {
            console.error('[VIEWERS] Error parsing message:', error)
          }
        }

        this.viewersWs.onerror = (error) => {
          console.error('[VIEWERS] WebSocket error:', error)
        }

        this.viewersWs.onclose = () => {
          console.log('[VIEWERS] Disconnected from live viewers server')

          // Attempt to reconnect after 5 seconds
          setTimeout(() => {
            if (!this.viewersWs || this.viewersWs.readyState === WebSocket.CLOSED) {
              console.log('[VIEWERS] Attempting to reconnect...')
              this.connectViewersWebSocket()
            }
          }, 5000)
        }
      } catch (error) {
        console.error('[VIEWERS] Failed to connect:', error)
      }
    },

    joinConversationViewers (conversationId) {
      if (!this.viewersWs || this.viewersWs.readyState !== WebSocket.OPEN) {
        console.warn('[VIEWERS] WebSocket not connected, cannot join')
        return
      }

      // Send join request - user info is verified on server via auth token
      const joinData = {
        action: 'join',
        conversation_id: conversationId
      }

      console.log('[VIEWERS] Joining conversation:', conversationId)
      this.viewersWs.send(JSON.stringify(joinData))

      // Start heartbeat
      if (this.heartbeatInterval) {
        clearInterval(this.heartbeatInterval)
      }

      this.heartbeatInterval = setInterval(() => {
        if (this.viewersWs && this.viewersWs.readyState === WebSocket.OPEN && this.selectedConversation) {
          this.viewersWs.send(JSON.stringify({
            action: 'heartbeat',
            conversation_id: this.selectedConversation.wa_id
          }))
        }
      }, 5000) // Every 5 seconds
    },

    leaveConversationViewers (conversationId) {
      if (!this.viewersWs || this.viewersWs.readyState !== WebSocket.OPEN) {
        return
      }

      // Send leave request - user ID is verified on server
      this.viewersWs.send(JSON.stringify({
        action: 'leave',
        conversation_id: conversationId
      }))

      // Stop heartbeat
      if (this.heartbeatInterval) {
        clearInterval(this.heartbeatInterval)
        this.heartbeatInterval = null
      }

      // Stop typing timeout
      if (this.typingTimeout) {
        clearTimeout(this.typingTimeout)
        this.typingTimeout = null
      }

      // Clear viewers list and typing users
      this.liveViewers = []
      this.typingUsers = []
    },

    disconnectViewersWebSocket () {
      // Leave current conversation if any
      if (this.selectedConversation) {
        this.leaveConversationViewers(this.selectedConversation.wa_id)
      }

      // Close WebSocket connection
      if (this.viewersWs) {
        this.viewersWs.close()
        this.viewersWs = null
      }

      // Stop heartbeat
      if (this.heartbeatInterval) {
        clearInterval(this.heartbeatInterval)
        this.heartbeatInterval = null
      }
    },

    // ===== Typing Indicator Methods =====

    onMessageInput () {
      this.handleTyping()
      this.$nextTick(() => {
        const el = this.$refs.messageTextarea
        if (el) {
          el.style.height = 'auto'
          el.style.height = Math.min(el.scrollHeight, 200) + 'px'
        }
      })
    },

    handleTyping () {
      if (!this.viewersWs || this.viewersWs.readyState !== WebSocket.OPEN || !this.selectedConversation) {
        return
      }

      // Send typing_start event - user info is verified on server
      this.viewersWs.send(JSON.stringify({
        action: 'typing_start',
        conversation_id: this.selectedConversation.wa_id
      }))

      // Clear previous timeout
      if (this.typingTimeout) {
        clearTimeout(this.typingTimeout)
      }

      // Set timeout to send typing_stop after 3 seconds of inactivity
      this.typingTimeout = setTimeout(() => {
        if (this.viewersWs && this.viewersWs.readyState === WebSocket.OPEN && this.selectedConversation) {
          this.viewersWs.send(JSON.stringify({
            action: 'typing_stop',
            conversation_id: this.selectedConversation.wa_id
          }))
        }
      }, 3000)
    },

    handleUserTypingEvent (data) {
      const userId = data.user_id
      const userName = data.user_name
      const userRole = data.user_role
      const isTyping = data.is_typing

      if (isTyping) {
        // Add user to typing list if not already there
        const existingIndex = this.typingUsers.findIndex(u => u.userId === userId)
        if (existingIndex === -1) {
          this.typingUsers.push({ userId, userName, userRole })
          console.log('[TYPING]', userName, 'is typing')
        }
      } else {
        // Remove user from typing list
        this.typingUsers = this.typingUsers.filter(u => u.userId !== userId)
        console.log('[TYPING]', userName, 'stopped typing')
      }
    },

    // ===== End Live Viewers Methods =====

    startAutoRefresh () {
      // Refresh conversations list every 15 seconds with background refresh to avoid loading UI
      this.refreshInterval = setInterval(() => {
        this.loadConversations(false, true) // Pass isBackgroundRefresh = true
      }, 15000)
    },

    stopAutoRefresh () {
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval)
        this.refreshInterval = null
      }
    },

    startMessagesAutoRefresh (waId) {
      // Refresh messages every 5 seconds for the active conversation
      this.messagesRefreshInterval = setInterval(() => {
        if (this.selectedConversation && this.selectedConversation.wa_id === waId && !this.sendingMessage) {
          this.loadConversationMessages(waId, true) // Pass isBackgroundRefresh = true
        }
      }, 5000)
    },

    stopMessagesAutoRefresh () {
      if (this.messagesRefreshInterval) {
        clearInterval(this.messagesRefreshInterval)
        this.messagesRefreshInterval = null
      }
    },

    handleImageSelect (event) {
      const file = event.target.files[0]
      if (file) {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          alert('Please select an image file')
          return
        }

        // Validate file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
          alert('Image size must be less than 10MB')
          return
        }

        // Create preview URL
        const preview = URL.createObjectURL(file)
        this.selectedImage = {
          file: file,
          preview: preview
        }
      }
    },

    removeSelectedImage () {
      if (this.selectedImage && this.selectedImage.preview && !this.selectedImage.fromQuickReply) {
        URL.revokeObjectURL(this.selectedImage.preview)
      }
      this.selectedImage = null
      this.selectedImageUrl = null
      if (this.$refs.imageInput) this.$refs.imageInput.value = ''
    },

    toggleAttachmentMenu () {
      this.showAttachmentMenu = !this.showAttachmentMenu
    },

    selectAttachmentType (type) {
      this.attachmentType = type
      this.showAttachmentMenu = false

      if (type === 'image') {
        this.$refs.imageInput.click()
      } else if (type === 'document') {
        this.$refs.documentInput.click()
      }
    },

    handleDocumentSelect (event) {
      const file = event.target.files[0]
      if (file) {
        // Validate file type
        const allowedExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv', 'zip', 'rar']
        const fileName = file.name.toLowerCase()
        const fileExtension = fileName.split('.').pop()

        if (!allowedExtensions.includes(fileExtension)) {
          alert('Please select a valid document file (PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT, CSV, ZIP, RAR)')
          return
        }

        // Validate file size (max 100MB)
        if (file.size > 100 * 1024 * 1024) {
          alert('Document size must be less than 100MB')
          return
        }

        this.selectedDocument = {
          file: file,
          name: file.name,
          size: file.size
        }
      }
    },

    removeSelectedDocument () {
      this.selectedDocument = null
      this.$refs.documentInput.value = ''
    },

    formatFileSize (bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
    },

    formatMessageText (text) {
      if (!text) return ''

      // Memoize — skip reprocessing identical strings (perf: called per message per render)
      if (!this._msgCache) this._msgCache = new Map()
      if (this._msgCache.has(text)) return this._msgCache.get(text)

      // 1. Escape HTML to prevent XSS
      const escaped = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')

      // 2. Detect URLs (http/https) first — so digits inside URLs are never matched as phones
      const withLinks = escaped.replace(
        /https?:\/\/[^\s<>"]+/gi,
        url => `<a href="${url}" target="_blank" rel="noopener noreferrer" class="msg-link">${url}</a>`
      )

      // 3. Detect any sequence of 4+ consecutive digits — wrap as copyable span
      //    Skips digit sequences already inside an <a> tag (href="...")
      const withPhones = withLinks.replace(/\d{4,}/g, (num, offset, str) => {
        // Check if this match sits inside an already-generated <a href="...">...</a>
        const before = str.slice(0, offset)
        const openA = before.lastIndexOf('<a ')
        const closeA = before.lastIndexOf('</a>')
        if (openA > closeA) return num // inside an anchor — leave it alone
        return `<span class="msg-phone" onclick="navigator.clipboard.writeText('${num}').then(()=>{this.classList.add('copied');setTimeout(()=>this.classList.remove('copied'),1500)})" title="Click to copy">${num}</span>`
      })

      // 4. Preserve line breaks
      const result = withPhones.replace(/\n/g, '<br>')

      // Cache and return (cap cache size to avoid memory leak on very long sessions)
      if (this._msgCache.size > 500) this._msgCache.clear()
      this._msgCache.set(text, result)
      return result
    },

    getDocumentName (url) {
      if (!url) return 'Document'
      try {
        const filename = url.split('/').pop()
        // Decode URL-encoded characters
        return decodeURIComponent(filename)
      } catch (error) {
        return 'Document'
      }
    },

    async loadQuickReplies () {
      this.loadingQuickReplies = true
      try {
        const result = await DashboardService.getQuickReplies()
        if (result.success) {
          this.quickReplies = result.quick_replies || []
        } else {
          console.error('Failed to load quick replies:', result.error)
          this.quickReplies = []
        }
      } catch (error) {
        console.error('Error loading quick replies:', error)
        this.quickReplies = []
      } finally {
        this.loadingQuickReplies = false
      }
    },

    async toggleQuickReplies () {
      this.showQuickRepliesDropdown = !this.showQuickRepliesDropdown

      // Load quick replies when opening dropdown if not already loaded
      if (this.showQuickRepliesDropdown && this.quickReplies.length === 0 && !this.loadingQuickReplies) {
        await this.loadQuickReplies()
      }
    },

    setReplyTo (message) {
      this.replyToMessage = message
      // Focus the input after selecting a reply target
      this.$nextTick(() => {
        const input = this.$el.querySelector('.message-input')
        if (input) input.focus()
      })
    },

    clearReply () {
      this.replyToMessage = null
    },

    getQuotedMessage (waMessageId) {
      return this.messageWaIdMap[waMessageId] || null
    },

    getQuotedPreview (waMessageId) {
      const m = this.getQuotedMessage(waMessageId)
      if (!m) return 'Message not in view'
      if (m.message) return m.message.length > 80 ? m.message.substring(0, 80) + '…' : m.message
      if (m.image_url) return '📷 Image'
      if (m.audio_url) return '🎵 Audio'
      if (m.document_url) return '📄 Document'
      if (m.video_url) return '🎥 Video'
      return '💬 Message'
    },

    quickAddToNotes (message) {
      // Pre-fill the note textarea with the message text and open the notes modal
      this.newNote = message.message
      this.showNotesModal = true
      // Load notes if not already loaded
      if (this.selectedConversation && this.customerNotes.length === 0 && this.notesCount > 0) {
        this.loadCustomerNotes(this.selectedConversation.wa_id)
      }
      // Focus the note textarea after the modal renders
      this.$nextTick(() => {
        const textarea = document.querySelector('.note-input')
        if (textarea) {
          textarea.focus()
          textarea.setSelectionRange(textarea.value.length, textarea.value.length)
        }
      })
    },

    useQuickReply (reply) {
      this.newMessage = reply.message || ''
      if (reply.image_url) {
        this.selectedImageUrl = reply.image_url
        this.selectedImage = { preview: reply.image_url, file: null, fromQuickReply: true }
      } else {
        this.selectedImageUrl = null
        this.selectedImage = null
      }
      this.showQuickRepliesDropdown = false

      const textarea = this.$el.querySelector('.message-input')
      if (textarea) textarea.focus()
    },

    handleClickOutside (event) {
      // Close quick replies dropdown when clicking outside
      const quickRepliesWrapper = this.$el?.querySelector('.quick-replies-wrapper')
      if (quickRepliesWrapper && !quickRepliesWrapper.contains(event.target)) {
        this.showQuickRepliesDropdown = false
      }
    },

    navigateToQuickReplies () {
      // Navigate to quick replies page
      this.$router.push('/quick-replies')
    },

    handleEnterKey (event) {
      // Send message on Enter (without Shift)
      if (!event.shiftKey) {
        this.sendMessage()
      } else {
        // Allow new line on Shift+Enter
        this.newMessage += '\n'
      }
    },

    async sendMessage () {
      if ((!this.newMessage.trim() && !this.selectedImage && !this.selectedDocument) || this.sendingMessage || !this.selectedConversation) {
        return
      }

      // Get token early — bail before touching UI if missing
      const token = AuthService.getAuthToken()
      if (!token) {
        alert('Authentication token not found. Please login again.')
        return
      }

      const dateTime = this.getCurrentLocalDateTime()
      const agentId = this.getAgentId()

      // Snapshot values before clearing UI (needed for API call + rollback)
      const savedMessage = this.newMessage.trim()
      const savedReplyId = this.replyToMessage ? this.replyToMessage.wa_message_id : null
      const savedReply = this.replyToMessage
      const savedImage = this.selectedImage // { file, preview }
      const savedDocument = this.selectedDocument // { file, name, size }
      const savedImageUrl = this.selectedImageUrl // URL string from quick reply

      // ── Optimistic message — show instantly ─────────────────────────
      const optimisticId = -Date.now()
      const optimisticMsg = {
        id: optimisticId,
        _optimistic: true,
        from: this.getSenderName(),
        to: this.selectedConversation.wa_id,
        message: savedMessage || null,
        image_url: savedImage ? savedImage.preview : (savedImageUrl || null), // blob URL or quick-reply URL
        document_url: savedDocument ? savedDocument.name : null,
        audio_url: null,
        video_url: null,
        sticker_url: null,
        date_time: dateTime,
        conversation_role: agentId ? 'Agent' : 'Owner',
        wa_message_id: null,
        reply_to_message_id: savedReplyId,
        agent_name: null
      }

      this.conversationMessages = [...this.conversationMessages, optimisticMsg]
      this.$nextTick(() => this.scrollToBottom(true))

      // Clear input immediately so user can type the next message
      this.newMessage = ''
      this.selectedImageUrl = null
      this.clearReply()
      // Clear attachment UI state but don't revoke blob URL yet (still used by optimistic bubble)
      if (savedImage) {
        this.selectedImage = null
        if (this.$refs.imageInput) this.$refs.imageInput.value = ''
      }
      if (savedDocument) {
        this.selectedDocument = null
        if (this.$refs.documentInput) this.$refs.documentInput.value = ''
      }
      // ────────────────────────────────────────────────────────────────

      try {
        this.sendingMessage = true

        let result

        if (savedImage && !savedImage.fromQuickReply) {
          result = await this.sendImageMessage(token, dateTime, savedMessage, savedImage.file, savedReplyId)
        } else if (savedImageUrl) {
          result = await this.sendImageUrlMessage(token, dateTime, savedMessage, savedImageUrl, savedReplyId)
        } else if (savedDocument) {
          result = await this.sendDocumentMessage(token, dateTime, savedMessage, savedDocument.file, savedReplyId)
        } else {
          result = await this.sendTextMessage(token, dateTime, savedMessage, savedReplyId)
        }

        if (result.success) {
          // Revoke blob URL now that server has the real image URL (skip for quick reply URLs)
          if (savedImage && savedImage.preview && !savedImage.fromQuickReply) URL.revokeObjectURL(savedImage.preview)
          // Background refresh to replace optimistic with real message — no await
          this.loadConversationMessages(this.selectedConversation.wa_id, true)
        } else {
          // Rollback: remove optimistic and restore input
          if (savedImage && savedImage.preview && !savedImage.fromQuickReply) URL.revokeObjectURL(savedImage.preview)
          this.conversationMessages = this.conversationMessages.filter(m => m.id !== optimisticId)
          this.newMessage = savedMessage
          this.selectedImageUrl = savedImageUrl
          this.selectedImage = savedImage || null
          this.replyToMessage = savedReply
          alert(`Failed to send message: ${result.error || 'Unknown error'}`)
        }
      } catch (error) {
        console.error('Error sending message:', error)
        if (savedImage && savedImage.preview && !savedImage.fromQuickReply) URL.revokeObjectURL(savedImage.preview)
        this.conversationMessages = this.conversationMessages.filter(m => m.id !== optimisticId)
        this.newMessage = savedMessage
        this.selectedImageUrl = savedImageUrl
        this.selectedImage = savedImage || null
        this.replyToMessage = savedReply
        alert('Failed to send message. Please try again.')
      } finally {
        this.sendingMessage = false
      }
    },

    async sendTextMessage (token, dateTime, message, replyToMessageId) {
      const agentId = this.getAgentId()
      const payload = {
        from: this.getSenderName(),
        to: this.selectedConversation.wa_id,
        message: message,
        date_time: dateTime,
        platform: 'whatsapp',
        wa_id: this.selectedConversation.wa_id,
        agent_id: agentId,
        conversation_role: agentId ? 'Agent' : 'Owner'
      }
      if (replyToMessageId) {
        payload.reply_to_message_id = replyToMessageId
      }
      const response = await fetch('https://api.quali-d.com/whatsapp-conversations/send', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      return await response.json()
    },

    async sendImageUrlMessage (token, dateTime, message, imageUrl, replyToMessageId) {
      const agentId = this.getAgentId()
      const payload = {
        from: this.getSenderName(),
        to: this.selectedConversation.wa_id,
        message: message || '',
        date_time: dateTime,
        platform: 'whatsapp',
        wa_id: this.selectedConversation.wa_id,
        agent_id: agentId,
        conversation_role: agentId ? 'Agent' : 'Owner',
        image_url: imageUrl
      }
      if (replyToMessageId) payload.reply_to_message_id = replyToMessageId

      const response = await fetch('https://api.quali-d.com/whatsapp-conversations/send', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      })

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)
      return await response.json()
    },

    async sendImageMessage (token, dateTime, message, imageFile, replyToMessageId) {
      const formData = new FormData()
      const agentId = this.getAgentId()
      formData.append('from', this.getSenderName())
      formData.append('to', this.selectedConversation.wa_id)
      formData.append('message', message || '')
      formData.append('date_time', dateTime)
      formData.append('platform', 'whatsapp')
      formData.append('wa_id', this.selectedConversation.wa_id)
      formData.append('agent_id', agentId || '')
      formData.append('conversation_role', agentId ? 'Agent' : 'Owner')
      formData.append('image', imageFile)
      if (replyToMessageId) {
        formData.append('reply_to_message_id', replyToMessageId)
      }

      const response = await fetch('https://api.quali-d.com/whatsapp-conversations/send', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      return await response.json()
    },

    async sendDocumentMessage (token, dateTime, message, docFile, replyToMessageId) {
      const formData = new FormData()
      const agentId = this.getAgentId()
      formData.append('from', this.getSenderName())
      formData.append('to', this.selectedConversation.wa_id)
      formData.append('message', message || '')
      formData.append('date_time', dateTime)
      formData.append('platform', 'whatsapp')
      formData.append('wa_id', this.selectedConversation.wa_id)
      formData.append('agent_id', agentId || '')
      formData.append('conversation_role', agentId ? 'Agent' : 'Owner')
      formData.append('document', docFile)
      if (replyToMessageId) {
        formData.append('reply_to_message_id', replyToMessageId)
      }

      const response = await fetch('https://api.quali-d.com/whatsapp-conversations/send', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      return await response.json()
    },

    async analyzeChat () {
      if (!this.selectedConversation || this.loadingAnalysis) return

      console.log('Starting chat analysis for:', this.selectedConversation.wa_id)

      try {
        this.loadingAnalysis = true
        const token = AuthService.getAuthToken()

        // Get current user/agent info from auth service
        const userData = AuthService.getUserData()
        const agentId = userData?.id || 1 // fallback to 1 if no agent ID available

        console.log('Request payload:', {
          wa_id: this.selectedConversation.wa_id,
          agent_id: agentId
        })

        const response = await fetch('https://api.quali-d.com/whatsapp-conversations/analyze', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            wa_id: this.selectedConversation.wa_id,
            agent_id: agentId
          })
        })

        console.log('Response status:', response.status)
        console.log('Response headers:', response.headers)

        if (!response.ok) {
          const errorText = await response.text()
          console.log('Error response:', errorText)

          // Handle 409 - Recently analyzed case
          if (response.status === 409) {
            try {
              const errorData = JSON.parse(errorText)
              if (errorData.suggestion && errorData.suggestion.includes('force_reanalyze')) {
                // Show modal for force re-analysis confirmation
                this.analysisModalData = {
                  message: errorData.message,
                  lastAnalysisTime: errorData.last_analysis_time,
                  token: token,
                  agentId: agentId
                }
                this.showAnalysisConfirmModal = true
                return // Exit here, modal will handle the rest
              }
            } catch (parseError) {
              // If can't parse error response, fall through to general error handling
              console.log('Could not parse 409 error response:', parseError)
            }
          }

          throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`)
        }

        const result = await response.json()
        console.log('Analysis result:', result)

        if (result.success) {
          // Show success notification
          this.showNotification({
            type: 'success',
            title: 'Analysis Complete',
            message: `Chat analysis completed for ${this.selectedConversation.from}`
          })

          // You can add additional logic here to display analysis results
          // For example, open a modal or navigate to analysis results
        } else {
          throw new Error(result.message || 'Analysis failed')
        }
      } catch (error) {
        console.error('Error analyzing chat:', error)
        this.showNotification({
          type: 'error',
          title: 'Analysis Failed',
          message: `Failed to analyze chat conversation. ${error.message || 'Please try again.'}`
        })
      } finally {
        this.loadingAnalysis = false
      }
    },

    // Customer Notes Modal Methods
    async openNotesModal () {
      this.showNotesModal = true
      if (this.selectedConversation && this.customerNotes.length === 0 && this.notesCount > 0) {
        await this.loadCustomerNotes(this.selectedConversation.wa_id)
      }
    },

    closeNotesModal () {
      this.showNotesModal = false
      this.editingNote = null
      this.editNoteText = ''
    },

    // Customer Notes Methods
    async loadCustomerNotesCount (waId) {
      // Lightweight load to get count for badge
      try {
        const token = AuthService.getAuthToken()
        const response = await fetch(`https://api.quali-d.com/customers/notes?phone_number=${encodeURIComponent(waId)}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (response.ok) {
          const result = await response.json()
          // Only store count for badge, not full notes
          this.notesCount = result.total_notes || 0
        } else {
          this.notesCount = 0
        }
      } catch (error) {
        console.error('Error loading customer notes count:', error)
        this.notesCount = 0
      }
    },

    async loadCustomerNotes (waId) {
      try {
        this.loadingNotes = true
        const token = AuthService.getAuthToken()

        const response = await fetch(`https://api.quali-d.com/customers/notes?phone_number=${encodeURIComponent(waId)}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const result = await response.json()
        this.customerNotes = result.original_format || []
      } catch (error) {
        console.error('Error loading customer notes:', error)
        this.customerNotes = []
      } finally {
        this.loadingNotes = false
      }
    },

    async addCustomerNote () {
      if (!this.newNote.trim() || !this.selectedConversation) return

      try {
        this.loadingNotes = true
        const token = AuthService.getAuthToken()
        const userData = AuthService.getUserData()

        const response = await fetch('https://api.quali-d.com/customers/notes', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            phone_number: this.selectedConversation.wa_id,
            note: this.newNote.trim(),
            added_by: userData?.id || 1
          })
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const result = await response.json()
        if (result.success) {
          this.newNote = ''
          await this.loadCustomerNotes(this.selectedConversation.wa_id)
        } else {
          alert('Failed to add note: ' + (result.message || 'Unknown error'))
        }
      } catch (error) {
        console.error('Error adding customer note:', error)
        alert('Failed to add note. Please try again.')
      } finally {
        this.loadingNotes = false
      }
    },

    startEditNote (note) {
      this.editingNote = note.id
      this.editNoteText = note.note
    },

    cancelNoteEdit () {
      this.editingNote = null
      this.editNoteText = ''
    },

    async saveNoteEdit (note) {
      if (!this.editNoteText.trim()) return

      try {
        const token = AuthService.getAuthToken()

        const response = await fetch('https://api.quali-d.com/customers/notes', {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            id: note.id,
            customer_id: note.customer_id,
            note: this.editNoteText.trim()
          })
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const result = await response.json()
        if (result.success) {
          this.cancelNoteEdit()
          await this.loadCustomerNotes(this.selectedConversation.wa_id)
        } else {
          alert('Failed to update note: ' + (result.message || 'Unknown error'))
        }
      } catch (error) {
        console.error('Error updating customer note:', error)
        alert('Failed to update note. Please try again.')
      }
    },

    async deleteCustomerNote (noteId) {
      if (!confirm('Are you sure you want to delete this note?')) return

      try {
        const token = AuthService.getAuthToken()

        const response = await fetch('https://api.quali-d.com/customers/notes', {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            id: noteId
          })
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const result = await response.json()
        if (result.success) {
          await this.loadCustomerNotes(this.selectedConversation.wa_id)
        } else {
          alert('Failed to delete note: ' + (result.message || 'Unknown error'))
        }
      } catch (error) {
        console.error('Error deleting customer note:', error)
        alert('Failed to delete note. Please try again.')
      }
    },

    formatDate (dateString) {
      if (!dateString) return ''
      try {
        return new Date(dateString).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      } catch (error) {
        return dateString
      }
    },

    // Chatbot Status Methods
    async loadChatbotStatus (waId) {
      try {
        const token = AuthService.getAuthToken()
        const response = await fetch(`https://api.quali-d.com/customers/chatbot-status?phone_number=${encodeURIComponent(waId)}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const result = await response.json()
        if (result.success) {
          this.chatbotStatus = result
        }
      } catch (error) {
        console.error('Error loading chatbot status:', error)
        this.chatbotStatus = null
      }
    },

    async toggleChatbot () {
      if (!this.selectedConversation || this.loadingChatbot) return

      // If chatbot is currently ON, show confirmation modal
      if (this.chatbotStatus?.bot_status) {
        this.showChatbotConfirmModal = true
        return
      }

      // If chatbot is OFF, turn it ON directly
      await this.performChatbotToggle()
    },

    closeChatbotConfirmModal () {
      this.showChatbotConfirmModal = false
    },

    async confirmTurnOffChatbot () {
      await this.performChatbotToggle()
      this.showChatbotConfirmModal = false
    },

    // Analysis Confirmation Modal Methods
    closeAnalysisConfirmModal () {
      this.showAnalysisConfirmModal = false
      this.analysisModalData = null
      this.loadingAnalysis = false
    },

    cancelAnalysis () {
      if (this.analysisModalData) {
        this.showNotification({
          type: 'info',
          title: 'Analysis Skipped',
          message: `Recent analysis available from ${this.analysisModalData.lastAnalysisTime}`
        })
      }
      this.closeAnalysisConfirmModal()
    },

    openCreateLeadModal () {
      if (!this.selectedConversation) {
        this.showNotification({
          type: 'error',
          title: 'No Conversation Selected',
          message: 'Please select a conversation first'
        })
        return
      }
      this.showCreateLeadModal = true
    },

    closeCreateLeadModal () {
      this.showCreateLeadModal = false
    },

    async loadAgentTemplatePermission () {
      try {
        const token = localStorage.getItem('agent_token')
        if (!token) return

        const response = await fetch('https://api.quali-d.com/whatsapp/templates/agent-permission', {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`
          }
        })

        if (response.ok) {
          const data = await response.json()
          this.allowAgentSendTemplate = data.allow_agent_send_template || false
        }
      } catch (error) {
        console.error('Error loading agent template permission:', error)
      }
    },

    async openQuickSendTemplateModal () {
      if (!this.selectedConversation) {
        this.showNotification({
          type: 'error',
          title: 'No Conversation Selected',
          message: 'Please select a conversation first'
        })
        return
      }
      this.showQuickSendTemplatePanel = true
      await this.loadTemplatesForPanel()
    },

    closeQuickSendTemplatePanel () {
      this.showQuickSendTemplatePanel = false
      this.resetTemplateForm()
    },

    resetTemplateForm () {
      this.templateForm = {
        selectedTemplateId: '',
        selectedTemplate: null,
        mediaUrl: '',
        variableMappings: []
      }
      this.templateError = ''
      this.templateSuccess = ''
    },

    async loadTemplatesForPanel () {
      this.loadingTemplates = true
      this.templateError = ''
      try {
        const token = localStorage.getItem('token') || localStorage.getItem('agent_token')
        const response = await fetch('https://api.quali-d.com/whatsapp/templates?status=APPROVED', {
          headers: { Authorization: `Bearer ${token}` }
        })
        const data = await response.json()
        if (data.success) {
          this.approvedTemplatesForPanel = data.templates || []
        } else {
          this.templateError = 'Failed to load templates'
        }
      } catch (error) {
        console.error('Error loading templates:', error)
        this.templateError = 'Failed to load templates'
      } finally {
        this.loadingTemplates = false
      }
    },

    onTemplateSelect () {
      const template = this.approvedTemplatesForPanel.find(t => t.id === this.templateForm.selectedTemplateId)
      this.templateForm.selectedTemplate = template
      this.templateForm.mediaUrl = ''
      this.templateForm.variableMappings = []
      this.templateError = ''
      this.templateSuccess = ''
    },

    getTemplateHeaderComponent () {
      if (!this.templateForm.selectedTemplate) return null
      return this.templateForm.selectedTemplate.components?.find(c => c.type === 'HEADER')
    },

    getTemplateBodyComponent () {
      if (!this.templateForm.selectedTemplate) return null
      return this.templateForm.selectedTemplate.components?.find(c => c.type === 'BODY')
    },

    getTemplateFooterComponent () {
      if (!this.templateForm.selectedTemplate) return null
      return this.templateForm.selectedTemplate.components?.find(c => c.type === 'FOOTER')
    },

    getTemplateButtonsComponent () {
      if (!this.templateForm.selectedTemplate) return null
      return this.templateForm.selectedTemplate.components?.find(c => c.type === 'BUTTONS')
    },

    hasMediaHeader () {
      const header = this.getTemplateHeaderComponent()
      return header && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(header.format)
    },

    getMediaHeaderType () {
      const header = this.getTemplateHeaderComponent()
      if (!header) return ''
      return header.format.charAt(0) + header.format.slice(1).toLowerCase()
    },

    getTemplateVariables () {
      const body = this.getTemplateBodyComponent()
      if (!body || !body.text) return []
      const matches = body.text.match(/\{\{(\d+)\}\}/g)
      if (!matches) return []
      return [...new Set(matches.map(m => m.replace(/\{\{|\}\}/g, '')))]
    },

    getOrCreateVariableMapping (varIndex) {
      let mapping = this.templateForm.variableMappings.find(m => m.variable_index === varIndex)
      if (!mapping) {
        mapping = {
          variable_index: varIndex,
          source_type: 'static_value',
          static_value: '',
          customer_field: '',
          default_value: ''
        }
        this.templateForm.variableMappings.push(mapping)
      }
      return mapping
    },

    formatVariableBadge (varIndex) {
      return `{{${varIndex}}}`
    },

    replaceTemplateVariables (text) {
      if (!text) return ''
      let result = text
      this.templateForm.variableMappings.forEach(mapping => {
        const varPattern = new RegExp(`\\{\\{${mapping.variable_index}\\}\\}`, 'g')
        let value = ''
        if (mapping.source_type === 'static_value') {
          value = mapping.static_value || `{{${mapping.variable_index}}}`
        } else if (mapping.source_type === 'customer_field') {
          value = `[${mapping.customer_field || mapping.variable_index}]`
        }
        result = result.replace(varPattern, `<strong>${value}</strong>`)
      })
      return result.replace(/\n/g, '<br>')
    },

    async sendQuickTemplate () {
      this.sendingTemplate = true
      this.templateError = ''
      this.templateSuccess = ''

      try {
        const token = localStorage.getItem('token') || localStorage.getItem('agent_token')

        // Get customer data for variable replacement
        const customerData = await this.fetchCustomerData(this.selectedConversation.wa_id)

        // Build body parameters
        const bodyParameters = []
        this.templateForm.variableMappings.forEach(mapping => {
          let value = ''
          if (mapping.source_type === 'static_value') {
            value = mapping.static_value
          } else if (mapping.source_type === 'customer_field') {
            value = customerData[mapping.customer_field] || mapping.default_value || ''
          }
          bodyParameters.push(value)
        })

        const payload = {
          phone_number: this.selectedConversation.wa_id,
          template_name: this.templateForm.selectedTemplate.name,
          template_language: this.templateForm.selectedTemplate.language,
          body_parameters: bodyParameters
        }

        // Add media URL if present
        const header = this.getTemplateHeaderComponent()
        if (header && header.format === 'IMAGE') {
          payload.image_url = this.templateForm.mediaUrl
        } else if (header && header.format === 'VIDEO') {
          payload.video_url = this.templateForm.mediaUrl
        } else if (header && header.format === 'DOCUMENT') {
          payload.document_url = this.templateForm.mediaUrl
        }

        const response = await fetch('https://api.quali-d.com/whatsapp/templates/send-single', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        })

        const data = await response.json()

        if (data.success) {
          this.templateSuccess = 'Template sent successfully!'
          this.showNotification({
            type: 'success',
            title: 'Template Sent',
            message: 'WhatsApp template has been sent successfully!'
          })

          setTimeout(() => {
            this.closeQuickSendTemplatePanel()
            if (this.selectedConversation) {
              this.selectConversation(this.selectedConversation)
            }
          }, 1500)
        } else {
          this.templateError = data.message || 'Failed to send template'
        }
      } catch (error) {
        console.error('Error sending template:', error)
        this.templateError = error.message || 'Failed to send template'
      } finally {
        this.sendingTemplate = false
      }
    },

    async fetchCustomerData (phoneNumber) {
      try {
        const token = localStorage.getItem('token') || localStorage.getItem('agent_token')
        const response = await fetch(`https://api.quali-d.com/customers/all?phone=${phoneNumber}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        const data = await response.json()
        if (data.success && data.customers && data.customers.length > 0) {
          return data.customers[0]
        }
        return {}
      } catch (error) {
        console.error('Error fetching customer data:', error)
        return {}
      }
    },

    openImageViewer (imageUrl) {
      this.viewerImageUrl = imageUrl
      this.showImageViewer = true
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden'
      // Add keyboard listener for ESC key
      document.addEventListener('keydown', this.handleImageViewerKeydown)
    },

    closeImageViewer () {
      this.showImageViewer = false
      this.viewerImageUrl = null
      // Restore body scroll
      document.body.style.overflow = ''
      // Remove keyboard listener
      document.removeEventListener('keydown', this.handleImageViewerKeydown)
    },

    handleImageViewerKeydown (event) {
      if (event.key === 'Escape' && this.showImageViewer) {
        this.closeImageViewer()
      }
    },

    // Media Gallery Methods
    async toggleMediaGallery () {
      if (this.showMediaGallery) {
        this.closeMediaGallery()
      } else {
        await this.openMediaGallery()
      }
    },

    async openMediaGallery () {
      this.showMediaGallery = true
      document.body.style.overflow = 'hidden'
      await this.loadConversationMedia()
    },

    closeMediaGallery () {
      this.showMediaGallery = false
      document.body.style.overflow = ''
    },

    async loadConversationMedia () {
      if (!this.selectedConversation) {
        return
      }

      try {
        this.loadingMedia = true

        const token = AuthService.getAuthToken()
        if (!token) {
          console.error('No authentication token found')
          return
        }

        const response = await fetch(
          `https://api.quali-d.com/whatsapp-conversation/media?wa_id=${this.selectedConversation.wa_id}`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        )

        const data = await response.json()

        if (data.success) {
          this.mediaGalleryData = data.media
          this.mediaGalleryTotals = data.totals
          console.log('Media loaded:', data.totals)
        } else {
          console.error('Failed to load media:', data.message)
        }
      } catch (error) {
        console.error('Error loading conversation media:', error)
      } finally {
        this.loadingMedia = false
      }
    },

    formatMediaDate (dateTime) {
      if (!dateTime) return ''
      try {
        const date = new Date(dateTime)
        const now = new Date()
        const diffInMs = now - date
        const diffInHours = diffInMs / (1000 * 60 * 60)

        if (diffInHours < 24) {
          // Show time for today
          return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
        } else if (diffInHours < 48) {
          return 'Yesterday'
        } else if (diffInHours < 168) {
          // Show day name for last week
          return date.toLocaleDateString('en-US', { weekday: 'short' })
        } else {
          // Show full date for older items
          return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
        }
      } catch (e) {
        return dateTime
      }
    },

    downloadMedia (url) {
      // Create a temporary link and trigger download
      const link = document.createElement('a')
      link.href = url
      link.target = '_blank'
      link.download = url.split('/').pop() || 'download'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },

    playVideo (url) {
      // Open video in a new tab or use existing video player
      window.open(url, '_blank')
    },

    openDocument (url) {
      window.open(url, '_blank')
    },

    // Forward Message Methods
    openForwardModal (message) {
      this.messageToForward = message
      this.showForwardModal = true
      this.selectedForwardConv = null
      this.forwardSearchQuery = ''
      document.body.style.overflow = 'hidden'
    },

    closeForwardModal () {
      this.showForwardModal = false
      this.messageToForward = null
      this.selectedForwardConv = null
      this.forwardSearchQuery = ''
      document.body.style.overflow = ''
    },

    selectForwardConversation (conversation) {
      // Don't allow selecting current conversation
      if (conversation.wa_id === this.selectedConversation?.wa_id) {
        return
      }
      this.selectedForwardConv = conversation
    },

    async confirmForward () {
      if (!this.selectedForwardConv || !this.messageToForward || this.forwarding) {
        return
      }

      try {
        this.forwarding = true

        const token = AuthService.getAuthToken()
        if (!token) {
          alert('Authentication token not found. Please login again.')
          return
        }

        const dateTime = this.getCurrentLocalDateTime()

        // Prepare the base payload
        const basePayload = {
          from: this.getSenderName(),
          to: this.selectedForwardConv.wa_id,
          date_time: dateTime,
          platform: 'whatsapp',
          wa_id: this.selectedForwardConv.wa_id,
          agent_id: this.getAgentId(),
          conversation_role: this.getAgentId() ? 'Agent' : 'Owner'
        }

        let result

        // Determine message type and send accordingly
        if (this.messageToForward.image_url) {
          // Forward image with URL
          result = await fetch('https://api.quali-d.com/whatsapp-conversations/send', {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              ...basePayload,
              message: this.messageToForward.message || '',
              image_url: this.messageToForward.image_url
            })
          })
        } else if (this.messageToForward.document_url) {
          // Forward document with URL
          result = await fetch('https://api.quali-d.com/whatsapp-conversations/send', {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              ...basePayload,
              message: this.messageToForward.message || '',
              document_url: this.messageToForward.document_url
            })
          })
        } else {
          // Forward text message
          result = await fetch('https://api.quali-d.com/whatsapp-conversations/send', {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              ...basePayload,
              message: this.messageToForward.message || ''
            })
          })
        }

        if (!result.ok) {
          throw new Error(`HTTP error! status: ${result.status}`)
        }

        const data = await result.json()

        if (data.success) {
          // Show success notification
          this.showNotification({
            type: 'success',
            title: 'Message Forwarded!',
            message: `Successfully forwarded to ${this.selectedForwardConv.from}`
          })

          // Close modal
          this.closeForwardModal()
        } else {
          throw new Error(data.error || 'Failed to forward message')
        }
      } catch (error) {
        console.error('Error forwarding message:', error)
        alert(`Failed to forward message: ${error.message}`)
      } finally {
        this.forwarding = false
      }
    },

    onLeadCreated (data) {
      console.log('Lead created:', data)

      // Mark that this conversation now has a lead
      this.conversationHasLead = true

      this.showNotification({
        type: 'success',
        title: data.was_existing ? 'Lead Updated!' : 'Lead Created!',
        message: data.was_existing
          ? `Successfully updated lead for ${this.selectedConversation.from}`
          : `Successfully created new lead for ${this.selectedConversation.from}`
      })

      // Mark the conversation as having a lead
      if (this.selectedConversation) {
        this.selectedConversation.has_lead = true
      }
    },

    async checkIfLeadExists (phoneNumber) {
      if (!phoneNumber) {
        this.conversationHasLead = false
        this.leadIsComplete = false
        return
      }

      try {
        const token = localStorage.getItem('auth_token')
        const response = await fetch(`https://api.quali-d.com/collected-leads/${phoneNumber}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (response.ok) {
          const data = await response.json()
          // Check if lead has any data (not just the default empty record)
          this.conversationHasLead = data.lead_data && (
            data.lead_data.name ||
            data.lead_data.email ||
            data.lead_data.company_name ||
            data.lead_data.is_complete
          )
          // Check if lead is marked as complete
          this.leadIsComplete = data.lead_data && data.lead_data.is_complete === 1
        } else {
          this.conversationHasLead = false
          this.leadIsComplete = false
        }
      } catch (error) {
        console.error('Error checking if lead exists:', error)
        this.conversationHasLead = false
        this.leadIsComplete = false
      }
    },

    async confirmForceAnalysis () {
      if (!this.analysisModalData || !this.selectedConversation) return

      try {
        const { token, agentId } = this.analysisModalData

        const retryResponse = await fetch('https://api.quali-d.com/whatsapp-conversations/analyze', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            wa_id: this.selectedConversation.wa_id,
            agent_id: agentId,
            force_reanalyze: true
          })
        })

        console.log('Force analysis response status:', retryResponse.status)

        if (!retryResponse.ok) {
          const retryErrorText = await retryResponse.text()
          console.log('Force analysis error response:', retryErrorText)
          throw new Error(`Force analysis failed! status: ${retryResponse.status}, message: ${retryErrorText}`)
        }

        const retryResult = await retryResponse.json()
        console.log('Force analysis result:', retryResult)

        if (retryResult.success) {
          this.showNotification({
            type: 'success',
            title: 'Analysis Complete',
            message: `Chat analysis completed for ${this.selectedConversation.from} (forced re-analysis)`
          })
        } else {
          throw new Error(retryResult.message || 'Force analysis failed')
        }
      } catch (error) {
        console.error('Error in force analysis:', error)
        this.showNotification({
          type: 'error',
          title: 'Analysis Failed',
          message: `Failed to force analyze conversation. ${error.message || 'Please try again.'}`
        })
      } finally {
        this.closeAnalysisConfirmModal()
      }
    },

    async performChatbotToggle () {
      if (!this.selectedConversation || this.loadingChatbot) return

      const wasActive = this.chatbotStatus?.bot_status

      try {
        this.loadingChatbot = true
        const token = AuthService.getAuthToken()

        const response = await fetch('https://api.quali-d.com/customers/chatbot-toggle', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            phone_number: this.selectedConversation.wa_id
          })
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const result = await response.json()
        if (result.success) {
          // Reload chatbot status to get updated state
          await this.loadChatbotStatus(this.selectedConversation.wa_id)

          // Show success notification
          this.showNotification({
            type: 'success',
            title: wasActive ? 'Chatbot Disabled' : 'Chatbot Enabled',
            message: wasActive
              ? `Chatbot has been turned OFF for ${this.selectedConversation.from}`
              : `Chatbot has been turned ON for ${this.selectedConversation.from}`
          })
        } else {
          throw new Error(result.message || 'Unknown error')
        }
      } catch (error) {
        console.error('Error toggling chatbot:', error)
        this.showNotification({
          type: 'error',
          title: 'Toggle Failed',
          message: 'Failed to toggle chatbot. Please try again.'
        })
      } finally {
        this.loadingChatbot = false
      }
    },

    async toggleBlockContact () {
      if (!this.selectedConversation || this.loadingBlock) return

      const isCurrentlyBlocked = this.selectedConversation.blocked === 1 || this.selectedConversation.blocked === true
      const phoneNumber = this.selectedConversation.wa_id

      try {
        this.loadingBlock = true

        let result
        if (isCurrentlyBlocked) {
          // Unblock the contact
          result = await WhatsAppService.unblockContact(phoneNumber)
        } else {
          // Block the contact
          result = await WhatsAppService.blockContact(phoneNumber)
        }

        if (result.success) {
          // Update the conversation's blocked status locally
          this.selectedConversation.blocked = isCurrentlyBlocked ? 0 : 1

          // Also update in the conversations list
          const conversationIndex = this.conversations.findIndex(c => c.wa_id === phoneNumber)
          if (conversationIndex !== -1) {
            this.conversations[conversationIndex].blocked = isCurrentlyBlocked ? 0 : 1
          }

          // Show success notification
          this.showNotification({
            type: 'success',
            title: isCurrentlyBlocked ? 'Contact Unblocked' : 'Contact Blocked',
            message: isCurrentlyBlocked
              ? `${this.selectedConversation.from} has been unblocked successfully.`
              : `${this.selectedConversation.from} has been blocked successfully.`
          })
        } else {
          throw new Error(result.message || 'Failed to update block status')
        }
      } catch (error) {
        console.error('Error toggling block status:', error)
        this.showNotification({
          type: 'error',
          title: 'Block/Unblock Failed',
          message: error.message || 'Failed to update block status. Please try again.'
        })
      } finally {
        this.loadingBlock = false
      }
    },

    async toggleReadStatus (conversation) {
      if (!conversation || this.loadingMarkUnread) return

      const waId = conversation.wa_id
      const isCurrentlyRead = conversation.read === 1 || conversation.read === true

      try {
        this.loadingMarkUnread = true

        // If currently read, mark as unread
        if (isCurrentlyRead) {
          const result = await WhatsAppService.markConversationAsUnread(waId)

          if (result.success) {
            // Update in the conversations list
            const conversationIndex = this.conversations.findIndex(c => c.wa_id === waId)
            if (conversationIndex !== -1) {
              this.conversations[conversationIndex].read = 0
            }

            // If this conversation is currently selected, update it too
            if (this.selectedConversation && this.selectedConversation.wa_id === waId) {
              this.selectedConversation.read = 0
            }

            // Show success notification
            this.showNotification({
              type: 'success',
              title: 'Marked as Unread',
              message: `Conversation with ${conversation.from} has been marked as unread.`
            })
          } else {
            throw new Error(result.message || 'Failed to mark conversation as unread')
          }
        } else {
          // If currently unread, mark as read by opening the conversation
          await this.selectConversation(conversation)

          this.showNotification({
            type: 'success',
            title: 'Marked as Read',
            message: `Conversation with ${conversation.from} has been marked as read.`
          })
        }
      } catch (error) {
        console.error('Error toggling read status:', error)
        this.showNotification({
          type: 'error',
          title: 'Toggle Read Status Failed',
          message: error.message || 'Failed to toggle read status. Please try again.'
        })
      } finally {
        this.loadingMarkUnread = false
      }
    },

    openContextMenu (event, conversation) {
      this.contextMenu.show = true
      this.contextMenu.x = event.clientX
      this.contextMenu.y = event.clientY
      this.contextMenu.conversation = conversation

      // Close context menu when clicking anywhere else
      document.addEventListener('click', this.closeContextMenu, { once: true })
    },

    closeContextMenu () {
      this.contextMenu.show = false
      this.contextMenu.conversation = null
    },

    async toggleArchiveFromContext (conversation) {
      if (!conversation) return

      const isArchived = conversation.archived === 1 || conversation.archived === true
      const token = localStorage.getItem('auth_token')

      try {
        const endpoint = isArchived
          ? 'https://api.quali-d.com/whatsapp-conversations/unarchive'
          : 'https://api.quali-d.com/whatsapp-conversations/archive'

        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            phone_number: conversation.wa_id
          })
        })

        const result = await response.json()

        if (result.success) {
          // Reload conversations immediately to reflect the change
          await this.loadConversations()

          this.showNotification({
            type: 'success',
            title: isArchived ? 'Conversation Unarchived' : 'Conversation Archived',
            message: `Conversation with ${conversation.from} has been ${isArchived ? 'unarchived' : 'archived'}.`
          })
        } else {
          throw new Error(result.message || 'Failed to update archive status')
        }
      } catch (error) {
        console.error('Error toggling archive:', error)
        this.showNotification({
          type: 'error',
          title: 'Archive Failed',
          message: error.message || 'Failed to update archive status. Please try again.'
        })
      }
    },

    async openTransferModal (conversation) {
      if (!conversation) return

      // Close context menu
      this.closeContextMenu()

      // Set transfer data
      this.transferData = {
        customerPhone: conversation.wa_id,
        customerName: conversation.from,
        currentDepartmentId: null,
        currentDepartmentName: '',
        newDepartmentId: '',
        language: 'ar' // Default to Arabic
      }

      // Load all departments first
      await this.loadAllDepartments()

      // Fetch customer's current department
      await this.loadCustomerDepartment(conversation.wa_id)

      // Show modal
      this.showTransferModal = true
      this.transferError = null
    },

    closeTransferModal () {
      this.showTransferModal = false
      this.transferData = {
        customerPhone: '',
        customerName: '',
        currentDepartmentId: null,
        currentDepartmentName: '',
        newDepartmentId: '',
        language: 'ar'
      }
      this.transferError = null
    },

    async loadCustomerDepartment (waId) {
      try {
        const token = localStorage.getItem('auth_token') || localStorage.getItem('agent_token')
        const response = await fetch(`https://api.quali-d.com/customer-service-department-mode/check?wa_id=${waId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Bearer ${token}`
          }
        })

        const result = await response.json()

        if (result.success && result.current_department_id) {
          this.transferData.currentDepartmentId = result.current_department_id
          // Find department name from allDepartments
          const dept = this.allDepartments.find(d => d.id === result.current_department_id)
          if (dept) {
            this.transferData.currentDepartmentName = dept.name
          }
        }
      } catch (error) {
        console.error('Error loading customer department:', error)
        // Non-critical error, don't show to user
      }
    },

    async loadAllDepartments () {
      try {
        const token = localStorage.getItem('auth_token') || localStorage.getItem('agent_token')
        const response = await fetch('https://api.quali-d.com/departments/all', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Bearer ${token}`
          }
        })

        const result = await response.json()

        if (result.success) {
          this.allDepartments = result.departments || []
        } else {
          throw new Error(result.message || 'Failed to load departments')
        }
      } catch (error) {
        console.error('Error loading departments:', error)
        this.transferError = 'Failed to load departments. Please try again.'
      }
    },

    async confirmTransfer () {
      if (!this.transferData.newDepartmentId) {
        this.transferError = 'Please select a department'
        return
      }

      this.transferLoading = true
      this.transferError = null

      try {
        const token = localStorage.getItem('auth_token') || localStorage.getItem('agent_token')
        const response = await fetch('https://api.quali-d.com/customers/transfer-department', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            wa_id: this.transferData.customerPhone,
            new_department_id: this.transferData.newDepartmentId,
            language: this.transferData.language
          })
        })

        const result = await response.json()

        if (result.success) {
          // Show success notification
          this.showNotification({
            type: 'success',
            title: 'Customer Transferred',
            message: `Customer has been transferred to ${result.new_department.name}`
          })

          // Close modal
          this.closeTransferModal()

          // Reload conversations to reflect the change
          await this.loadConversations()

          // Send browser notification to agents in new department
          if ('Notification' in window && Notification.permission === 'granted') {
            // eslint-disable-next-line no-new
            new Notification('Customer Transferred', {
              body: `${this.transferData.customerName} has been transferred to your department`,
              icon: 'https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg'
            })
          }
        } else {
          throw new Error(result.message || 'Failed to transfer customer')
        }
      } catch (error) {
        console.error('Error transferring customer:', error)
        this.transferError = error.message || 'Failed to transfer customer. Please try again.'
      } finally {
        this.transferLoading = false
      }
    },

    showNotification (notification) {
      this.notification = notification
      // Auto-dismiss after 5 seconds
      setTimeout(() => {
        this.dismissNotification()
      }, 5000)
    },

    dismissNotification () {
      this.notification = null
    },

    async checkWhatsAppCredentials () {
      try {
        // If user is an agent, they don't need their own WhatsApp credentials
        // They use the company's WhatsApp setup
        if (this.isAgent) {
          this.hasWhatsAppCredentials = true
          return
        }

        const token = AuthService.getAuthToken()
        if (!token) {
          this.hasWhatsAppCredentials = false
          return
        }

        const response = await fetch('https://api.quali-d.com/whatsapp/has-credentials', {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json'
          }
        })

        if (!response.ok) {
          // Fallback: if we can't check credentials but we have a session ID, assume credentials exist
          const sessionId = localStorage.getItem('whatsapp_unique_session_id')
          this.hasWhatsAppCredentials = !!sessionId
          return
        }

        const data = await response.json()
        this.hasWhatsAppCredentials = data.has_credentials === true
      } catch (error) {
        console.error('Error checking WhatsApp credentials:', error)
        // Fallback: if we can't check credentials but we have a session ID, assume credentials exist
        const sessionId = localStorage.getItem('whatsapp_unique_session_id')
        this.hasWhatsAppCredentials = !!sessionId
      }
    },

    goToWhatsAppTutorial () {
      this.$router.push('/tutorials/whatsapp')
    },

    async checkDepartmentMode () {
      try {
        // Get token - could be auth_token (owner) or agent_token (agent)
        const token = localStorage.getItem('auth_token') || localStorage.getItem('agent_token')

        if (!token) {
          console.warn('No token found for department mode check')
          this.departmentModeEnabled = false
          return
        }

        // Call department mode settings endpoint (supports both user and agent tokens)
        const response = await fetch('https://api.quali-d.com/customer-service-department-mode/settings', {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
            'Content-Type': 'application/json'
          }
        })

        const settings = await response.json()

        if (settings.success && settings.customer_service_department_mode === 1) {
          this.departmentModeEnabled = true

          // Fetch departments list
          const deptsResponse = await fetch('https://api.quali-d.com/departments/list-for-selection', {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: 'application/json',
              'Content-Type': 'application/json'
            }
          })

          const deptsData = await deptsResponse.json()
          if (deptsData.success && deptsData.departments) {
            this.departments = deptsData.departments
          }
        } else {
          this.departmentModeEnabled = false
          this.departments = []
        }
      } catch (error) {
        console.error('Error checking department mode:', error)
        this.departmentModeEnabled = false
        this.departments = []
      }
    },

    // Audio player methods
    toggleAudio (messageId, audioUrl) {
      const audioElement = this.$refs[`audio-${messageId}`]
      if (!audioElement || !audioElement[0]) return

      const audio = audioElement[0]

      if (this.playingAudioId === messageId) {
        // Pause current audio
        audio.pause()
        this.playingAudioId = null
      } else {
        // Stop any currently playing audio
        if (this.playingAudioId !== null) {
          const currentAudio = this.$refs[`audio-${this.playingAudioId}`]
          if (currentAudio && currentAudio[0]) {
            currentAudio[0].pause()
            currentAudio[0].currentTime = 0
          }
        }
        // Play new audio
        audio.play()
        this.playingAudioId = messageId
      }
    },

    onAudioLoaded (messageId) {
      const audioElement = this.$refs[`audio-${messageId}`]
      if (!audioElement || !audioElement[0]) return

      const audio = audioElement[0]
      const duration = Math.floor(audio.duration)
      this.audioDurations[messageId] = duration
    },

    onAudioTimeUpdate (messageId) {
      const audioElement = this.$refs[`audio-${messageId}`]
      if (!audioElement || !audioElement[0]) return

      const audio = audioElement[0]
      this.audioCurrentTimes[messageId] = Math.floor(audio.currentTime)
    },

    onAudioEnded (messageId) {
      this.playingAudioId = null
      const audioElement = this.$refs[`audio-${messageId}`]
      if (audioElement && audioElement[0]) {
        audioElement[0].currentTime = 0
      }
    },

    getAudioDuration (messageId) {
      if (this.playingAudioId === messageId && this.audioCurrentTimes[messageId]) {
        const remaining = (this.audioDurations[messageId] || 0) - this.audioCurrentTimes[messageId]
        return this.formatAudioTime(remaining)
      }
      return this.formatAudioTime(this.audioDurations[messageId] || 0)
    },

    formatAudioTime (seconds) {
      if (!seconds || isNaN(seconds)) return '0:00'
      const mins = Math.floor(seconds / 60)
      const secs = seconds % 60
      return `${mins}:${secs.toString().padStart(2, '0')}`
    },

    getWaveformHeight (index) {
      // Generate pseudo-random heights for waveform visualization
      const heights = [30, 50, 40, 60, 35, 55, 45, 65, 40, 50, 35, 60, 50, 45, 55, 40, 60, 35, 50, 45, 55, 40, 60, 50, 35, 55, 45, 60, 40, 50]
      return heights[index - 1] || 40
    }
  }
}
</script>

<style scoped>
.whatsapp-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-secondary);
  width: 100%;
  max-width: 100vw;
  overflow-x: hidden;
  transition: background-color 0.3s ease;
}

.main-content {
  flex: 1;
  margin-left: 280px;
  display: flex;
  flex-direction: column;
}

.page-content {
  flex: 1;
  padding: 32px;
  position: relative;
}

/* WhatsApp Disabled Overlay */
.whatsapp-disabled-overlay {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1000;
  background: white;
  border-radius: 24px;
  padding: 48px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  text-align: center;
  max-width: 500px;
  width: 90%;
  animation: modalSlideIn 0.4s ease-out;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
}

.disabled-overlay-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.disabled-whatsapp-icon {
  width: 80px;
  height: 80px;
  opacity: 0.9;
  filter: drop-shadow(0 4px 8px rgba(37, 211, 102, 0.2));
}

.disabled-overlay-title {
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.disabled-overlay-message {
  font-size: 16px;
  color: #6b7280;
  line-height: 1.6;
  margin: 0;
  max-width: 400px;
}

.setup-whatsapp-btn {
  background: linear-gradient(135deg, #25d366 0%, #128c7e 100%);
  color: white;
  border: none;
  padding: 14px 32px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  box-shadow: 0 4px 6px -1px rgba(37, 211, 102, 0.3);
}

.setup-whatsapp-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 12px -1px rgba(37, 211, 102, 0.4);
}

.setup-whatsapp-btn:active {
  transform: translateY(0);
}

.whatsapp-container.blurred {
  filter: blur(8px);
  pointer-events: none;
  user-select: none;
}

.whatsapp-container {
  background: var(--bg-primary);
  border-radius: 16px;
  border: 1px solid var(--border-primary);
  overflow: hidden;
  max-height: calc(100vh - 120px);
  display: flex;
  flex-direction: row;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

.whatsapp-container.split-view .conversations-panel {
  flex: 0 0 auto;
  width: clamp(260px, 30%, 380px);
  border-right: 1px solid var(--border-primary);
}

.whatsapp-container:not(.split-view) .conversations-panel {
  width: 100%;
}

.conversations-panel {
  display: flex;
  flex-direction: column;
  background: var(--bg-primary);
  transition: all 0.3s ease;
}

.conversation-detail-panel {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  background: var(--bg-primary);
}

/* Filters and Search */
.filters-container {
  padding: 20px 24px;
  border-bottom: 1px solid var(--border-primary);
  background: var(--bg-secondary);
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
  transition: background-color 0.3s ease, border-color 0.3s ease;
  flex-shrink: 0;
}

.search-box {
  display: flex;
  align-items: center;
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  padding: 8px 12px;
  flex: 1;
  min-width: 200px;
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

.search-icon {
  color: var(--text-tertiary);
  margin-right: 8px;
}

.search-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 14px;
  color: var(--text-primary);
  background: transparent;
}

.search-input::placeholder {
  color: var(--text-tertiary);
}

.filter-controls {
  display: flex;
  gap: 12px;
}

.filter-select {
  padding: 8px 12px;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  background: var(--bg-primary);
  font-size: 14px;
  color: var(--text-primary);
  cursor: pointer;
  transition: all 0.3s ease;
}

.archive-toggle-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  background: var(--bg-primary);
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.archive-toggle-btn:hover {
  background: var(--bg-secondary);
  border-color: var(--border-secondary);
  color: var(--text-primary);
}

.archive-toggle-btn.active {
  background: #25D366;
  border-color: #25D366;
  color: white;
}

.archive-toggle-btn.active:hover {
  background: #22c55e;
  border-color: #22c55e;
}

.archive-text {
  font-weight: 500;
}

/* Unread Toggle Button */
.unread-toggle-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  background: var(--bg-primary);
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.unread-toggle-btn:hover {
  background: var(--bg-secondary);
  border-color: var(--border-secondary);
  color: var(--text-primary);
}

.unread-toggle-btn.active {
  background: #3B82F6;
  border-color: #3B82F6;
  color: white;
}

.unread-toggle-btn.active:hover {
  background: #2563EB;
  border-color: #2563EB;
}

.unread-text {
  font-weight: 500;
}

/* Notification Toggle Button */
.notification-toggle-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  background: var(--bg-primary);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s;
  min-width: 40px;
  height: 40px;
}

.notification-toggle-btn:hover {
  background: var(--bg-secondary);
  border-color: var(--border-secondary);
  color: var(--text-primary);
}

.notification-toggle-btn.active {
  background: #3B82F6;
  border-color: #3B82F6;
  color: white;
}

.notification-toggle-btn.active:hover {
  background: #2563EB;
  border-color: #2563EB;
}

/* Conversations List */
.conversations-list-container {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0;
  width: 100%;
  max-width: 100%;
}

.conversations-list {
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;
  max-width: 100%;
}

.conversation-card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid var(--border-primary);
  cursor: pointer;
  transition: all 0.2s ease;
  background: var(--bg-primary);
  border-radius: 0;
  border: none;
  position: relative;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;
}

.conversation-card:hover {
  background: var(--bg-secondary);
  border-bottom-color: #3b82f6;
  transform: translateX(2px);
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.1);
}

.conversation-card.selected {
  background: rgba(59, 130, 246, 0.1) !important;
  border-left: 4px solid #3b82f6 !important;
  transform: translateX(4px);
}

.conversation-card.unread {
  background: rgba(37, 211, 102, 0.1) !important;
  border-left: 4px solid #25D366 !important;
  font-weight: 500;
}

.conversation-card.unread:hover {
  background: rgba(37, 211, 102, 0.15) !important;
  border-left-color: #25D366 !important;
}

/* Dark mode specific styling for unread messages */
.dark .conversation-card.unread {
  background: rgba(37, 211, 102, 0.15) !important;
}

.dark .conversation-card.unread:hover {
  background: rgba(37, 211, 102, 0.2) !important;
}

.conversation-card.needs-agent {
  background: var(--bg-primary);
  border-left: 3px solid #f59e0b;
}

.conversation-card.needs-agent:hover {
  background: var(--bg-secondary);
  border-left-color: #d97706;
}

.conversation-card.vip {
  background: var(--bg-primary);
  border-left: 3px solid #8b5cf6;
}

.conversation-card.vip:hover {
  background: var(--bg-secondary);
  border-left-color: #7c3aed;
}

.conversation-card.blocked {
  background: rgba(239, 68, 68, 0.05);
  border-left: 3px solid #ef4444;
  opacity: 0.85;
}

.conversation-card.blocked:hover {
  background: rgba(239, 68, 68, 0.08);
  border-left-color: #dc2626;
  opacity: 1;
}

.dark .conversation-card.blocked {
  background: rgba(239, 68, 68, 0.08);
  border-left-color: #ef4444;
}

.dark .conversation-card.blocked:hover {
  background: rgba(239, 68, 68, 0.12);
  border-left-color: #f87171;
}

/* Avatar */
.conversation-avatar {
  position: relative;
  flex-shrink: 0;
}

.avatar-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.avatar-circle.whatsapp {
  background: linear-gradient(135deg, #25D366, #128C7E);
}

.vip-badge {
  position: absolute;
  top: -2px;
  right: -2px;
  background: #8b5cf6;
  color: white;
  font-size: 9px;
  font-weight: 600;
  padding: 1px 4px;
  border-radius: 6px;
  border: 1px solid var(--bg-primary);
}

/* Content */
.conversation-content {
  flex: 1;
  min-width: 0;
  max-width: calc(100% - 60px);
  overflow: hidden;
}

.conversation-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.contact-name {
  font-size: 16px;
  font-weight: 500;
  color: var(--text-primary);
  margin: 0 0 2px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.conversation-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.unread-indicator {
  width: 10px;
  height: 10px;
  background: #25D366;
  border-radius: 50%;
  box-shadow: 0 0 0 2px var(--bg-primary), 0 2px 4px rgba(37, 211, 102, 0.3);
  animation: pulse-green 2s infinite;
}

@keyframes pulse-green {
  0% {
    box-shadow: 0 0 0 2px var(--bg-primary), 0 2px 4px rgba(37, 211, 102, 0.3), 0 0 0 0 rgba(37, 211, 102, 0.7);
  }
  70% {
    box-shadow: 0 0 0 2px var(--bg-primary), 0 2px 4px rgba(37, 211, 102, 0.3), 0 0 0 6px rgba(37, 211, 102, 0);
  }
  100% {
    box-shadow: 0 0 0 2px var(--bg-primary), 0 2px 4px rgba(37, 211, 102, 0.3), 0 0 0 0 rgba(37, 211, 102, 0);
  }
}

.timestamp {
  font-size: 12px;
  color: var(--text-tertiary);
  white-space: nowrap;
}

.last-message {
  margin-bottom: 4px;
}

.media-indicator {
  color: var(--text-tertiary);
  font-style: italic;
  font-size: 13px;
}

.message-text {
  color: var(--text-secondary);
  font-size: 13px;
  line-height: 1.3;
  word-break: break-word;
  max-width: 100%;
  white-space: pre-wrap;
}

/* Clamp to 1 line only in the conversation list preview */
.conversation-card .message-text {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: normal;
}

/* WhatsApp Meta */
.whatsapp-meta {
  display: none; /* Hide metadata in compact mode */
}

.meta-row {
  display: flex;
  gap: 6px;
  font-size: 11px;
}

.meta-label {
  color: var(--text-tertiary);
  font-weight: 500;
  min-width: 60px;
}

.meta-value {
  color: var(--text-secondary);
  font-family: monospace;
}

/* Status Indicators */
.status-indicators {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex-shrink: 0;
  align-items: flex-end;
  max-width: 120px;
}

.status-badge {
  padding: 1px 6px;
  border-radius: 8px;
  font-size: 9px;
  font-weight: 500;
  text-align: center;
  white-space: nowrap;
}

.needs-agent-badge {
  background: #fef9e7;
  color: #b45309;
  display: flex;
  align-items: center;
  gap: 4px;
  position: relative;
}

.bell-icon {
  position: relative;
}

.notification-dot {
  position: absolute;
  top: -2px;
  right: -2px;
  width: 6px;
  height: 6px;
  background: #ef4444;
  border-radius: 50%;
  border: 1px solid var(--bg-primary);
}

.vip-badge-main {
  background: rgba(124, 58, 237, 0.1);
  color: #7c3aed;
}

/* Dark mode specific styling for VIP badges */
.dark .vip-badge-main {
  background: rgba(124, 58, 237, 0.15);
  color: #a855f7;
}

.blocked-badge {
  background: rgba(239, 68, 68, 0.1);
  color: #dc2626;
  display: flex;
  align-items: center;
  gap: 4px;
  border: 1px solid rgba(239, 68, 68, 0.2);
}

.dark .blocked-badge {
  background: rgba(239, 68, 68, 0.15);
  color: #ef4444;
  border-color: rgba(239, 68, 68, 0.25);
}

/* Loading and Empty States */
.loading-state, .empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 64px;
  color: var(--text-secondary);
  text-align: center;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--border-primary);
  border-top: 3px solid #25D366;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-icon {
  font-size: 48px;
  opacity: 0.5;
}

.empty-state h3 {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

/* Infinite Scroll Load More */
.load-more-indicator {
  display: flex;
  justify-content: center;
  padding: 16px;
}

.load-more-spinner {
  width: 24px;
  height: 24px;
  border: 2px solid var(--border-primary);
  border-top-color: #25D366;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

/* Conversation Detail Panel */
.conversation-detail-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid var(--border-primary);
  background: var(--bg-secondary);
  flex-shrink: 0;
}

.header-info {
  display: flex;
  align-items: center;
  gap: 16px;
}

.contact-avatar {
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #25D366, #128C7E);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.contact-details h3.contact-name {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 4px 0;
}

.contact-id {
  font-size: 13px;
  color: var(--text-tertiary);
  margin: 0;
  font-family: monospace;
}

/* Live Viewers Indicator */
.live-viewers-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  padding: 6px 10px;
  background: rgba(37, 211, 102, 0.08);
  border-radius: 12px;
  border: 1px solid rgba(37, 211, 102, 0.2);
  width: fit-content;
}

.viewer-avatars {
  display: flex;
  gap: 4px;
}

.viewer-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: linear-gradient(135deg, #25D366 0%, #20c05a 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 12px;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(37, 211, 102, 0.3);
  transition: transform 0.2s ease;
}

.viewer-avatar:hover {
  transform: scale(1.1);
}

.viewer-count {
  font-size: 12px;
  color: #25D366;
  font-weight: 600;
  white-space: nowrap;
}

/* Typing Indicator */
.typing-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: rgba(37, 211, 102, 0.05);
  border-radius: 8px;
  margin-bottom: 8px;
  font-size: 13px;
  color: #666;
}

.typing-text {
  font-style: italic;
}

.typing-dots {
  display: flex;
  gap: 3px;
  align-items: center;
}

.typing-dots .dot {
  width: 5px;
  height: 5px;
  background: #25D366;
  border-radius: 50%;
  animation: typing-bounce 1.4s infinite ease-in-out;
}

.typing-dots .dot:nth-child(1) {
  animation-delay: -0.32s;
}

.typing-dots .dot:nth-child(2) {
  animation-delay: -0.16s;
}

@keyframes typing-bounce {
  0%, 80%, 100% {
    transform: scale(0);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

.close-conversation-btn {
  width: 40px;
  height: 40px;
  border: none;
  background: var(--bg-primary);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--text-secondary);
  transition: all 0.2s ease;
}

.close-conversation-btn:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.mobile-back-btn {
  display: none;
  width: 40px;
  height: 40px;
  border: none;
  background: transparent;
  border-radius: 8px;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--text-primary);
  transition: all 0.2s ease;
  padding: 0;
  margin-right: 8px;
  flex-shrink: 0;
}

.mobile-back-btn:hover {
  background: var(--bg-tertiary);
}

.mobile-back-btn:active {
  transform: scale(0.95);
}

.conversation-messages-container {
  flex: 1;
  overflow-y: auto;
  padding: 0;
}

.messages-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 64px;
  color: var(--text-secondary);
}

.no-messages {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 64px;
  color: var(--text-secondary);
  text-align: center;
}

.no-messages-icon {
  font-size: 48px;
  opacity: 0.5;
}

.messages-list {
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.message-item {
  padding: 16px;
  border-radius: 12px;
  border: 1px solid var(--border-primary);
  transition: all 0.3s ease;
}

.message-item.message-agent,
.message-item.message-owner {
  background: rgba(37, 211, 102, 0.1);
  border-left: 4px solid #25D366;
  margin-left: 0;
  margin-right: 40px;
}

.message-item.message-customer {
  background: rgba(59, 130, 246, 0.1);
  border-left: 4px solid #3b82f6;
  margin-left: 40px;
  margin-right: 0;
}

.message-item.message-pending {
  opacity: 0.6;
}

.message-header {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  gap: 8px;
  flex-wrap: wrap;
}

.message-sender {
  font-weight: 600;
  font-size: 14px;
  color: var(--text-primary);
}

.message-time {
  font-size: 10px;
  color: var(--text-tertiary);
  font-family: monospace;
  align-self: flex-end;
  margin-top: 4px;
  margin-bottom: -4px;
  opacity: 0.7;
  line-height: 1;
}

.message-content {
  margin-bottom: 4px;
  display: flex;
  flex-direction: column;
}

.message-content p {
  font-size: 14px;
  line-height: 1.5;
  color: var(--text-secondary);
  margin: 0;
  word-wrap: break-word;
  white-space: pre-wrap;
}

/* Clickable links inside messages — :deep() required because v-html bypasses scoped styles */
:deep(.msg-link) {
  color: #128C7E;
  font-weight: 600;
  text-decoration: underline;
  word-break: break-all;
}
:deep(.msg-link:hover) {
  color: #25D366;
}

/* Clickable number sequences inside messages */
:deep(.msg-phone) {
  color: #128C7E;
  font-weight: 600;
  text-decoration: underline dotted;
  cursor: pointer;
  border-radius: 3px;
  padding: 0 2px;
  transition: background 0.15s, color 0.15s;
}
:deep(.msg-phone:hover) {
  background: rgba(18, 140, 126, 0.12);
  color: #25D366;
}
:deep(.msg-phone.copied) {
  background: rgba(37, 211, 102, 0.15);
  color: #128C7E;
}
:deep(.msg-phone.copied::after) {
  content: ' ✓';
  font-size: 11px;
  font-weight: 700;
}

.message-meta {
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
}

.agent-info {
  font-size: 12px;
  color: var(--text-tertiary);
  font-style: italic;
}

/* Message Media */
.message-image {
  margin-bottom: 8px;
}

.message-img {
  max-width: 300px;
  max-height: 400px;
  border-radius: 8px;
  object-fit: cover;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.message-img:hover {
  transform: scale(1.02);
}

/* Message Videos */
.message-video {
  margin-bottom: 8px;
}

.message-vid {
  max-width: 400px;
  max-height: 500px;
  border-radius: 8px;
  background: var(--bg-tertiary);
  outline: none;
}

.message-vid:focus {
  outline: 2px solid #3b82f6;
}

/* Message Audio (Voice Notes) - Custom Player */
.message-audio {
  margin-bottom: 8px;
}

.voice-note-player {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: var(--bg-tertiary);
  border-radius: 20px;
  max-width: 280px;
  transition: background-color 0.3s ease;
}

.voice-play-btn {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: #25D366;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(37, 211, 102, 0.2);
}

.voice-play-btn:hover {
  background: #1fb855;
  transform: scale(1.05);
  box-shadow: 0 4px 8px rgba(37, 211, 102, 0.3);
}

.voice-play-btn:active {
  transform: scale(0.95);
}

.voice-play-btn.playing {
  background: #ef4444;
  box-shadow: 0 2px 4px rgba(239, 68, 68, 0.2);
}

.voice-play-btn.playing:hover {
  background: #dc2626;
  box-shadow: 0 4px 8px rgba(239, 68, 68, 0.3);
}

.voice-waveform {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 2px;
  height: 32px;
  overflow: hidden;
}

.waveform-bar {
  flex: 1;
  background: var(--text-tertiary);
  border-radius: 2px;
  min-width: 2px;
  transition: all 0.15s ease;
  opacity: 0.4;
}

.voice-note-player:hover .waveform-bar {
  opacity: 0.6;
}

.voice-play-btn.playing ~ .voice-waveform .waveform-bar {
  opacity: 0.7;
  background: #25D366;
  animation: waveAnimation 1.2s ease-in-out infinite;
}

@keyframes waveAnimation {
  0%, 100% {
    transform: scaleY(1);
  }
  50% {
    transform: scaleY(1.3);
  }
}

.waveform-bar:nth-child(2n) {
  animation-delay: 0.1s;
}

.waveform-bar:nth-child(3n) {
  animation-delay: 0.2s;
}

.waveform-bar:nth-child(5n) {
  animation-delay: 0.3s;
}

.voice-duration {
  flex-shrink: 0;
  font-size: 12px;
  color: var(--text-secondary);
  font-family: monospace;
  min-width: 40px;
  text-align: right;
  transition: color 0.3s ease;
}

/* Message Stickers */
.message-sticker {
  margin-bottom: 8px;
}

.message-sticker-img {
  max-width: 200px;
  max-height: 200px;
  object-fit: contain;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.message-sticker-img:hover {
  transform: scale(1.05);
}

/* Document Message Styles */
.message-document {
  margin-bottom: 8px;
}

.document-link {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: var(--hover-background);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  text-decoration: none;
  color: var(--text-primary);
  transition: all 0.2s ease;
  max-width: 300px;
}

.document-link:hover {
  background: var(--border-primary);
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.document-icon {
  flex-shrink: 0;
  color: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
}

.document-info-message {
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow: hidden;
}

.document-filename {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.document-action {
  font-size: 12px;
  color: var(--text-secondary);
}

/* Message Input Container */
.message-input-container {
  border-top: 1px solid var(--border-primary);
  background: var(--bg-secondary);
  padding: 16px 20px;
  flex-shrink: 0;
}

.blocked-contact-warning {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  margin-bottom: 12px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 12px;
  color: #dc2626;
  font-size: 14px;
  line-height: 1.5;
}

.dark .blocked-contact-warning {
  background: rgba(239, 68, 68, 0.15);
  border-color: rgba(239, 68, 68, 0.25);
  color: #ef4444;
}

.blocked-contact-warning svg {
  flex-shrink: 0;
}

.blocked-contact-warning span {
  flex: 1;
}

.message-input-form {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  padding: 8px;
  transition: border-color 0.2s ease;
}

.message-input-form:focus-within {
  border-color: #25D366;
}

.attach-file-btn {
  width: 40px;
  height: 40px;
  border: none;
  background: transparent;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--text-secondary);
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.attach-file-btn:hover:not(:disabled) {
  background: var(--bg-secondary);
  color: #25D366;
}

.attach-file-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Quick Replies */
.quick-replies-wrapper {
  position: relative;
  flex-shrink: 0;
}

.add-quick-replies-btn,
.quick-replies-btn {
  width: 40px;
  height: 40px;
  border: none;
  background: transparent;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--text-secondary);
  transition: all 0.2s ease;
  position: relative;
}

.add-quick-replies-btn:hover:not(:disabled),
.quick-replies-btn:hover:not(:disabled) {
  background: var(--bg-secondary);
  color: #25D366;
}

.add-quick-replies-btn:disabled,
.quick-replies-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.quick-replies-count {
  position: absolute;
  top: 2px;
  right: 2px;
  background: #25D366;
  color: white;
  font-size: 10px;
  font-weight: 600;
  min-width: 16px;
  height: 16px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
}

.quick-replies-dropdown {
  position: absolute;
  bottom: 50px;
  left: 0;
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 320px;
  max-width: 400px;
  max-height: 400px;
  overflow-y: auto;
  z-index: 100;
}

.dropdown-loading {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 24px;
  color: var(--text-secondary);
}

.loading-spinner-small {
  width: 20px;
  height: 20px;
  border: 2px solid var(--border-primary);
  border-top-color: #25D366;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

.dropdown-empty {
  padding: 24px;
  text-align: center;
}

.dropdown-empty p {
  color: var(--text-secondary);
  margin: 0 0 12px 0;
}

.create-link {
  display: inline-block;
  color: #25D366;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  padding: 8px 16px;
  border: 1px solid #25D366;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.create-link:hover {
  background: #25D366;
  color: white;
}

.quick-replies-list {
  padding: 8px;
}

.quick-reply-item {
  padding: 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.quick-reply-item:hover {
  background: var(--bg-secondary);
}

.quick-reply-item:last-child {
  margin-bottom: 0;
}

.reply-thumbnail {
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 6px;
  flex-shrink: 0;
  border: 1px solid var(--border-primary);
}

.reply-text-wrap {
  flex: 1;
  min-width: 0;
}

.reply-title {
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 4px;
  font-size: 14px;
}

.reply-preview {
  font-size: 13px;
  color: var(--text-secondary);
  line-height: 1.4;
}

.reply-image-only {
  font-style: italic;
}

.image-preview {
  position: relative;
  flex-shrink: 0;
}

.preview-img {
  width: 60px;
  height: 60px;
  border-radius: 8px;
  object-fit: cover;
  border: 1px solid var(--border-primary);
}

.remove-image-btn {
  position: absolute;
  top: -6px;
  right: -6px;
  width: 20px;
  height: 20px;
  border: none;
  background: #ef4444;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 12px;
  transition: background-color 0.2s ease;
}

.remove-image-btn:hover {
  background: #dc2626;
}

/* Attachment Menu Styles */
.attachment-wrapper {
  position: relative;
}

.attachment-menu {
  position: absolute;
  bottom: 100%;
  left: 0;
  margin-bottom: 8px;
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 100;
  min-width: 150px;
  overflow: hidden;
}

.attachment-menu-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border: none;
  background: transparent;
  color: var(--text-primary);
  cursor: pointer;
  transition: background-color 0.2s ease;
  font-size: 14px;
}

.attachment-menu-item:not(:last-child) {
  border-bottom: 1px solid var(--border-primary);
}

.attachment-menu-item:hover {
  background: var(--hover-background);
}

.attachment-menu-item svg {
  flex-shrink: 0;
}

/* Document Preview Styles */
.document-preview {
  position: relative;
  flex-shrink: 0;
  background: var(--hover-background);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  padding: 8px 12px;
  max-width: 250px;
}

.document-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.document-info svg {
  flex-shrink: 0;
  color: var(--primary-color);
}

.document-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow: hidden;
}

.document-name {
  font-size: 13px;
  font-weight: 500;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.document-size {
  font-size: 11px;
  color: var(--text-secondary);
}

.remove-document-btn {
  position: absolute;
  top: -6px;
  right: -6px;
  width: 20px;
  height: 20px;
  border: none;
  background: #ef4444;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 12px;
  transition: background-color 0.2s ease;
}

.remove-document-btn:hover {
  background: #dc2626;
}

.input-wrapper {
  flex: 1;
  min-width: 0;
}

.message-input {
  width: 100%;
  border: none;
  outline: none;
  background: transparent;
  color: var(--text-primary);
  font-size: 14px;
  line-height: 1.4;
  resize: none;
  min-height: 24px;
  max-height: 200px;
  overflow-y: auto;
  font-family: inherit;
  padding: 8px 0;
}

.message-input::placeholder {
  color: var(--text-tertiary);
}

.send-message-btn {
  width: 40px;
  height: 40px;
  border: none;
  background: #25D366;
  color: white;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.send-message-btn:hover:not(:disabled) {
  background: #22c55e;
  transform: scale(1.05);
}

.send-message-btn:disabled {
  background: var(--bg-tertiary);
  color: var(--text-tertiary);
  cursor: not-allowed;
  transform: none;
}

.sending-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

/* Header Actions */
.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.notes-btn {
  position: relative;
  width: 40px;
  height: 40px;
  border: none;
  background: var(--bg-primary);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--text-secondary);
  transition: all 0.2s ease;
}

.notes-btn:hover {
  background: var(--bg-secondary);
  color: #3b82f6;
}

.notes-badge {
  position: absolute;
  top: -2px;
  right: -2px;
  background: #3b82f6;
  color: white;
  font-size: 10px;
  font-weight: 600;
  padding: 1px 5px;
  border-radius: 10px;
  border: 1px solid var(--bg-primary);
  min-width: 16px;
  text-align: center;
}

.create-lead-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: none;
  /* Default: Blue gradient for incomplete leads */
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  border-radius: 8px;
  color: white;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.2);
}

.create-lead-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
}

/* Green gradient when lead is complete */
.create-lead-btn.lead-complete {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.2);
}

.create-lead-btn.lead-complete:hover {
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
}

.create-lead-btn svg {
  flex-shrink: 0;
}

.create-lead-text {
  white-space: nowrap;
}

@media (max-width: 768px) {
  .create-lead-text {
    display: none;
  }

  .create-lead-btn {
    padding: 10px;
    width: 40px;
    height: 40px;
    justify-content: center;
  }
}

.chatbot-btn {
  width: 44px;
  height: 44px;
  border: none;
  background: var(--bg-primary);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--text-tertiary);
  transition: all 0.2s ease;
  position: relative;
}

.chatbot-btn:hover:not(:disabled) {
  background: var(--bg-secondary);
  transform: scale(1.05);
}

.chatbot-btn.active {
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
  border: 1px solid rgba(59, 130, 246, 0.2);
}

.chatbot-btn.active:hover {
  background: rgba(59, 130, 246, 0.15);
  border-color: rgba(59, 130, 246, 0.3);
}

.chatbot-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.block-btn {
  width: 44px;
  height: 44px;
  border: none;
  background: var(--bg-primary);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--text-tertiary);
  transition: all 0.2s ease;
  position: relative;
}

.block-btn:hover:not(:disabled) {
  background: var(--bg-secondary);
  color: #ef4444;
  transform: scale(1.05);
}

.block-btn.blocked {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  border: 1px solid rgba(239, 68, 68, 0.2);
}

.block-btn.blocked:hover:not(:disabled) {
  background: rgba(239, 68, 68, 0.15);
  border-color: rgba(239, 68, 68, 0.3);
  color: #22c55e;
}

.block-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

/* Context Menu */
.context-menu {
  position: fixed;
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 10000;
  min-width: 180px;
  padding: 4px 0;
}

.context-menu-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  cursor: pointer;
  color: var(--text-primary);
  transition: all 0.2s;
}

.context-menu-item:hover {
  background: var(--bg-secondary);
}

.context-menu-item svg {
  color: var(--text-secondary);
  flex-shrink: 0;
}

.context-menu-item:hover svg {
  color: var(--text-primary);
}

.context-menu-item span {
  font-size: 14px;
  font-weight: 500;
}

.quick-send-template-btn {
  height: 42px;
  min-width: 180px;
  padding: 0 16px;
  border: none;
  background: var(--bg-primary);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  color: var(--text-secondary);
  transition: all 0.2s ease;
  position: relative;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
}

.quick-send-template-btn:hover {
  background: var(--bg-secondary);
  color: #3B82F6;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
}

.quick-send-text {
  color: inherit;
}

.analyze-btn {
  height: 42px;
  min-width: 160px;
  padding: 0 16px;
  border: none;
  background: var(--bg-primary);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  color: var(--text-secondary);
  transition: all 0.2s ease;
  position: relative;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
}

.analyze-btn:hover:not(:disabled) {
  background: var(--bg-secondary);
  color: #22c55e;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(34, 197, 94, 0.15);
}

.analyze-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.analyze-text {
  font-size: 14px;
  font-weight: 500;
}

.loading-spinner-small {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(59, 130, 246, 0.3);
  border-top: 2px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

/* Confirmation Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.confirm-modal {
  background: var(--bg-primary);
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  width: 100%;
  max-width: 450px;
  overflow: hidden;
}

.confirm-modal-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 24px;
  border-bottom: 1px solid var(--border-primary);
  background: var(--bg-secondary);
}

.warning-icon {
  font-size: 24px;
}

.confirm-modal-header h3 {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.confirm-modal-content {
  padding: 24px;
}

.confirm-modal-content p {
  margin: 0 0 16px 0;
  color: var(--text-secondary);
  line-height: 1.5;
}

.confirm-modal-content p:last-child {
  margin-bottom: 0;
}

.customer-info {
  background: var(--bg-secondary);
  padding: 12px 16px;
  border-radius: 8px;
  border-left: 4px solid #3b82f6;
}

.customer-info span {
  font-weight: 600;
  color: var(--text-primary);
}

.warning-text {
  color: #ef4444 !important;
  font-weight: 500;
  font-size: 14px;
}

.info-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(59, 130, 246, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  margin-right: 12px;
}

.analysis-info {
  background: var(--bg-secondary);
  padding: 12px 16px;
  border-radius: 8px;
  border-left: 4px solid #22c55e;
}

.analysis-info span {
  font-weight: 600;
  color: var(--text-primary);
  font-family: monospace;
}

.info-text {
  color: var(--text-primary) !important;
  font-weight: 500;
  font-size: 14px;
}

.confirm-modal-actions {
  display: flex;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid var(--border-primary);
  background: var(--bg-secondary);
}

.confirm-modal-actions .cancel-btn {
  flex: 1;
  padding: 10px 20px;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  background: var(--bg-primary);
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.confirm-modal-actions .cancel-btn:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.confirm-modal-actions .confirm-btn {
  flex: 1;
  padding: 10px 20px;
  border: 1px solid #ef4444;
  border-radius: 8px;
  background: #ef4444;
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.confirm-modal-actions .confirm-btn:hover:not(:disabled) {
  background: #dc2626;
  border-color: #dc2626;
}

.confirm-modal-actions .confirm-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Transfer Department Modal */
.transfer-modal {
  background: var(--bg-primary);
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  width: 100%;
  max-width: 500px;
  overflow: hidden;
}

.transfer-modal .modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid var(--border-primary);
  background: var(--bg-secondary);
}

.transfer-modal .modal-header h3 {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.close-modal-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s;
}

.close-modal-btn:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.transfer-modal .modal-body {
  padding: 24px;
}

.transfer-info {
  margin: 0 0 20px 0;
  color: var(--text-secondary);
  line-height: 1.5;
  font-size: 14px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group:last-child {
  margin-bottom: 0;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: var(--text-primary);
  font-size: 14px;
}

.form-group .required {
  color: #ef4444;
}

.form-group .customer-info {
  background: var(--bg-secondary);
  padding: 12px 16px;
  border-radius: 8px;
  border-left: 4px solid #3b82f6;
}

.form-group .customer-info strong {
  display: block;
  color: var(--text-primary);
  margin-bottom: 4px;
  font-size: 15px;
}

.form-group .customer-info .customer-phone {
  color: var(--text-secondary);
  font-size: 13px;
}

.current-department-info {
  background: var(--bg-secondary);
  padding: 12px 16px;
  border-radius: 8px;
  border-left: 4px solid #10b981;
  color: var(--text-primary);
  font-weight: 500;
  font-size: 14px;
}

.form-control {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  background: var(--bg-primary);
  color: var(--text-primary);
  font-size: 14px;
  transition: border-color 0.2s;
}

.form-control:focus {
  outline: none;
  border-color: #3b82f6;
}

.error-message {
  margin-top: 16px;
  padding: 12px 16px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 8px;
  color: #ef4444;
  font-size: 14px;
}

.transfer-modal .modal-footer {
  display: flex;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid var(--border-primary);
  background: var(--bg-secondary);
}

.transfer-modal .btn {
  flex: 1;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.transfer-modal .btn-secondary {
  background: var(--bg-primary);
  color: var(--text-primary);
  border: 1px solid var(--border-primary);
}

.transfer-modal .btn-secondary:hover {
  background: var(--bg-tertiary);
}

.transfer-modal .btn-primary {
  background: #3b82f6;
  color: white;
  border: 1px solid #3b82f6;
}

.transfer-modal .btn-primary:hover:not(:disabled) {
  background: #2563eb;
  border-color: #2563eb;
}

.transfer-modal .btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Notification Toast */
.notification-toast {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 1200;
  background: var(--bg-primary);
  border-radius: 12px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  border: 1px solid var(--border-primary);
  min-width: 350px;
  max-width: 450px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  animation: slideInRight 0.3s ease-out;
}

.notification-toast.success {
  border-left: 4px solid #10b981;
}

.notification-toast.error {
  border-left: 4px solid #ef4444;
}

.notification-icon {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;
}

.notification-toast.success .notification-icon {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
}

.notification-toast.error .notification-icon {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.notification-content {
  flex: 1;
  min-width: 0;
}

.notification-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 4px;
}

.notification-message {
  font-size: 13px;
  color: var(--text-secondary);
  line-height: 1.4;
}

.notification-close {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--text-tertiary);
  transition: all 0.2s ease;
}

.notification-close:hover {
  background: var(--bg-secondary);
  color: var(--text-secondary);
}

@keyframes slideInRight {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

/* Notes Modal */
.notes-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.notes-modal {
  background: var(--bg-primary);
  border-radius: 16px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  width: 100%;
  max-width: 600px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}

.notes-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid var(--border-primary);
}

.notes-modal-header h3 {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.modal-header-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.customer-name {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
}

.customer-id {
  font-size: 12px;
  color: var(--text-tertiary);
  font-family: monospace;
}

.modal-close-btn {
  width: 40px;
  height: 40px;
  border: none;
  background: var(--bg-secondary);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--text-secondary);
  transition: all 0.2s ease;
}

.modal-close-btn:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.notes-modal-content {
  padding: 20px 24px;
  overflow-y: auto;
  flex: 1;
}

.add-note-form {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
}

.note-input, .edit-note-input {
  width: 100%;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 14px;
  color: var(--text-primary);
  background: var(--bg-primary);
  resize: vertical;
  min-height: 60px;
  font-family: inherit;
  transition: border-color 0.2s ease;
}

.note-input:focus, .edit-note-input:focus {
  outline: none;
  border-color: #25D366;
}

.note-input::placeholder {
  color: var(--text-tertiary);
}

.add-note-btn {
  align-self: flex-end;
  padding: 8px 16px;
  border: none;
  background: #25D366;
  color: white;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.add-note-btn:hover:not(:disabled) {
  background: #22c55e;
}

.add-note-btn:disabled {
  background: var(--bg-tertiary);
  color: var(--text-tertiary);
  cursor: not-allowed;
}

.notes-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.notes-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 20px;
  justify-content: center;
  color: var(--text-secondary);
}

.no-notes {
  text-align: center;
  padding: 40px 20px;
  color: var(--text-tertiary);
}

.no-notes-icon {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
}

.no-notes p {
  font-size: 16px;
  font-weight: 500;
  color: var(--text-secondary);
  margin: 0 0 8px 0;
}

.no-notes small {
  font-size: 14px;
  color: var(--text-tertiary);
}

.note-item {
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  background: var(--bg-primary);
  overflow: hidden;
}

.note-display {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 12px;
}

.note-content {
  flex: 1;
  min-width: 0;
}

.note-content p {
  margin: 0 0 8px 0;
  color: var(--text-primary);
  font-size: 14px;
  line-height: 1.4;
  word-wrap: break-word;
}

.note-meta {
  display: flex;
  gap: 12px;
  font-size: 12px;
  color: var(--text-tertiary);
}

.note-actions {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
  margin-left: 8px;
}

.edit-note-btn, .delete-note-btn {
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.edit-note-btn {
  color: #3b82f6;
}

.edit-note-btn:hover {
  background: rgba(59, 130, 246, 0.1);
}

.delete-note-btn {
  color: #ef4444;
}

.delete-note-btn:hover {
  background: rgba(239, 68, 68, 0.1);
}

.edit-note-form {
  padding: 12px;
}

.edit-note-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 8px;
}

.save-btn, .cancel-btn {
  padding: 6px 12px;
  border: 1px solid var(--border-primary);
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.save-btn {
  background: #25D366;
  color: white;
  border-color: #25D366;
}

.save-btn:hover {
  background: #22c55e;
  border-color: #22c55e;
}

.cancel-btn {
  background: var(--bg-secondary);
  color: var(--text-secondary);
}

.cancel-btn:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

/* Dark mode specific styles */
.dark .message-item.message-agent,
.dark .message-item.message-owner {
  background: rgba(37, 211, 102, 0.15);
}

.dark .message-item.message-customer {
  background: rgba(59, 130, 246, 0.15);
}

/* In split-view: compact filter area */
.whatsapp-container.split-view .filters-container {
  padding: 12px 16px;
  gap: 8px;
}

.whatsapp-container.split-view .filter-controls {
  flex-wrap: wrap;
  gap: 6px;
}

/* Hide notification button and All Conversations select in split-view */
.whatsapp-container.split-view .notification-toggle-btn,
.whatsapp-container.split-view .filter-select:not(.department-filter) {
  display: none;
}

/* Icon-only archive/unread buttons in split-view */
.whatsapp-container.split-view .archive-text,
.whatsapp-container.split-view .unread-text {
  display: none;
}

.whatsapp-container.split-view .archive-toggle-btn,
.whatsapp-container.split-view .unread-toggle-btn {
  padding: 8px;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
    padding-top: 64px;
    width: 100%;
    max-width: 100vw;
    overflow-x: hidden;
  }

  .page-content {
    padding: 16px;
  }

  .whatsapp-container {
    border-radius: 8px;
  }

  .filters-container {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .filter-controls {
    flex-direction: column;
    gap: 8px;
  }

  .conversation-card {
    padding: 12px 16px;
    gap: 10px;
    overflow: hidden;
  }

  .conversation-card.selected {
    transform: translateX(2px);
  }

  .avatar-circle {
    width: 36px;
    height: 36px;
  }

  .contact-name {
    font-size: 15px;
  }

  .conversation-content {
    max-width: calc(100% - 90px);
  }

  .status-indicators {
    max-width: 80px;
    position: relative;
    z-index: 1;
  }

  .conversation-meta {
    position: relative;
    z-index: 2;
    flex-wrap: wrap;
    align-items: flex-start;
  }

  .conversation-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .needs-agent-badge {
    font-size: 8px;
    padding: 1px 4px;
    gap: 2px;
  }

  .needs-agent-badge .bell-icon {
    width: 10px;
    height: 10px;
  }

  .needs-agent-badge .notification-dot {
    width: 4px;
    height: 4px;
    top: -1px;
    right: -1px;
  }

  .whatsapp-meta {
    display: none;
  }

  .pagination {
    padding: 16px;
  }

  /* Mobile adjustments */
  .whatsapp-container.split-view {
    flex-direction: column;
  }

  /* Mobile conversation view - hide list when conversation is selected */
  .conversations-panel.mobile-hidden {
    display: none !important;
  }

  .conversation-detail-panel.mobile-full-width {
    width: 100% !important;
    height: calc(100vh - 120px) !important;
    max-height: none !important;
    position: relative;
    z-index: 10;
    display: flex !important;
    flex-direction: column;
  }

  /* When no conversation is selected on mobile */
  .whatsapp-container:not(.mobile-conversation-view) .conversations-panel {
    width: 100%;
    min-width: auto;
    border-right: none;
    height: calc(100vh - 120px);
  }

  /* Ensure conversation container takes full space on mobile */
  .whatsapp-container.mobile-conversation-view {
    height: calc(100vh - 120px);
    overflow: hidden;
  }

  .conversation-detail-header {
    padding: 16px 20px;
    flex-shrink: 0;
  }

  .mobile-back-btn {
    display: flex !important;
  }

  .close-conversation-btn {
    display: none !important;
  }

  .header-info {
    gap: 12px;
    flex: 1;
  }

  .contact-avatar {
    width: 44px;
    height: 44px;
  }

  .contact-name {
    font-size: 16px !important;
  }

  .contact-id {
    font-size: 12px;
  }

  .conversation-messages-container {
    flex: 1;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  .messages-list {
    padding: 16px 20px;
    gap: 12px;
  }

  .message-item.message-agent,
  .message-item.message-owner {
    margin-right: 20px;
  }

  .message-item.message-customer {
    margin-left: 20px;
  }

  .message-item {
    padding: 12px;
  }

  .close-conversation-btn {
    width: 40px;
    height: 40px;
    background: var(--bg-tertiary) !important;
    border: 1px solid var(--border-primary);
  }

  .header-actions {
    gap: 8px;
    flex-wrap: nowrap;
  }

  .analyze-btn .analyze-text {
    display: none;
  }

  .analyze-btn {
    padding: 0 12px;
  }

  /* Mobile Message Input */
  .message-input-container {
    padding: 12px 16px;
  }

  .message-input-form {
    gap: 8px;
    padding: 6px;
  }

  .attach-file-btn, .send-message-btn, .quick-replies-btn, .add-quick-replies-btn {
    width: 36px;
    height: 36px;
  }

  .quick-replies-dropdown {
    min-width: 280px;
    max-width: calc(100vw - 32px);
  }

  .preview-img {
    width: 50px;
    height: 50px;
  }

  .message-input {
    font-size: 16px; /* Prevents zoom on iOS */
  }

  /* Mobile Notes Modal */
  .notes-modal-overlay {
    padding: 10px;
  }

  .notes-modal {
    max-height: 90vh;
    max-width: 100%;
  }

  .notes-modal-header {
    padding: 16px 20px;
    flex-wrap: wrap;
    gap: 8px;
  }

  .notes-modal-header h3 {
    font-size: 18px;
  }

  .modal-header-info {
    order: 2;
    align-items: flex-start;
  }

  .modal-close-btn {
    width: 36px;
    height: 36px;
  }

  .notes-modal-content {
    padding: 16px 20px;
  }

  .note-input, .edit-note-input {
    font-size: 16px; /* Prevents zoom on iOS */
    min-height: 80px;
  }

  .note-actions {
    flex-direction: column;
    gap: 4px;
  }

  .edit-note-btn, .delete-note-btn {
    width: 32px;
    height: 32px;
  }

  .edit-note-actions {
    flex-direction: column;
    gap: 8px;
  }

  .save-btn, .cancel-btn {
    padding: 10px 16px;
    font-size: 14px;
  }
}

/* Quick Send Template Panel Styles */
.template-panel-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: flex-end;
  z-index: 9999;
  backdrop-filter: blur(2px);
}

.template-panel {
  width: 480px;
  max-width: 90vw;
  height: 100vh;
  background: var(--bg-primary);
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  animation: slideInRight 0.3s ease;
}

@keyframes slideInRight {
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
}

.template-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid var(--border-color);
  background: var(--bg-primary);
}

.template-panel-header h3 {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.close-panel-btn {
  background: transparent;
  border: none;
  padding: 8px;
  cursor: pointer;
  color: var(--text-secondary);
  border-radius: 6px;
  transition: all 0.2s;
}

.close-panel-btn:hover {
  background: var(--hover-bg);
  color: var(--text-primary);
}

.template-panel-content {
  flex: 1;
  overflow-y: auto;
  padding: 0;
}

.panel-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
}

.loading-animation {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.loading-dots {
  display: flex;
  gap: 8px;
}

.loading-dots .dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #25D366;
  animation: dotPulse 1.4s ease-in-out infinite;
}

.loading-dots .dot:nth-child(2) {
  animation-delay: 0.2s;
}

.loading-dots .dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes dotPulse {
  0%, 100% {
    transform: scale(0.8);
    opacity: 0.5;
  }
  50% {
    transform: scale(1.2);
    opacity: 1;
  }
}

.loading-text {
  color: var(--text-secondary);
  font-size: 14px;
  margin: 0;
}

.panel-form {
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.section-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.preview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--border-color);
}

.preview-label {
  font-size: 11px;
  font-weight: 700;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.6px;
}

.recipient-phone {
  font-size: 12px;
  color: var(--text-tertiary);
  font-family: monospace;
  background: var(--hover-bg);
  padding: 4px 10px;
  border-radius: 12px;
}

.panel-select,
.panel-input {
  padding: 10px 14px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  font-size: 14px;
  color: var(--text-primary);
  background: var(--bg-secondary);
  transition: border-color 0.2s;
}

.panel-select:focus,
.panel-input:focus {
  outline: none;
  border-color: #3B82F6;
}

.template-preview-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 12px;
}

.simple-preview-bubble {
  background: #DCF8C6;
  border-radius: 8px;
  padding: 10px 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  max-width: 100%;
}

.bubble-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.message-header-preview {
  margin-bottom: 6px;
}

.header-text-preview {
  font-weight: 600;
  font-size: 14px;
  color: #1F2937;
  margin-bottom: 6px;
}

.header-media-preview {
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 6px;
}

.preview-image {
  width: 100%;
  height: auto;
  display: block;
}

.media-placeholder {
  padding: 30px 16px;
  text-align: center;
  background: rgba(0, 0, 0, 0.05);
  color: #65676B;
  font-size: 20px;
  border: 1px dashed rgba(0, 0, 0, 0.1);
  border-radius: 6px;
}

.message-body-preview p {
  font-size: 14px;
  line-height: 1.5;
  color: #1F2937;
  margin: 0;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.message-footer-preview {
  font-size: 12px;
  color: #65676B;
  margin-top: 6px;
  font-style: italic;
}

.message-buttons-preview {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
}

.preview-button {
  background: rgba(59, 130, 246, 0.1);
  border: none;
  padding: 10px 12px;
  text-align: center;
  color: #3B82F6;
  font-size: 13px;
  font-weight: 600;
  cursor: default;
  border-radius: 6px;
  transition: background 0.2s;
}

.preview-button:hover {
  background: rgba(59, 130, 246, 0.15);
}

.variables-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.variable-card {
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 14px;
}

.variable-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  gap: 12px;
  flex-wrap: wrap;
}

.variable-badge {
  background: #3B82F6;
  color: white;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  font-family: monospace;
}

.variable-type-selector {
  display: flex;
  gap: 12px;
}

.type-radio {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-size: 13px;
  color: var(--text-secondary);
}

.type-radio input[type="radio"] {
  cursor: pointer;
}

.variable-inputs {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.panel-select-sm,
.panel-input-sm {
  padding: 8px 12px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 13px;
  color: var(--text-primary);
  background: var(--bg-primary);
}

.panel-select-sm:focus,
.panel-input-sm:focus {
  outline: none;
  border-color: #3B82F6;
}

.error-banner,
.success-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
}

.error-banner {
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: #EF4444;
}

.success-banner {
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(34, 197, 94, 0.3);
  color: #22C55E;
}

.send-template-btn {
  width: 100%;
  padding: 12px 24px;
  background: #25D366;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.send-template-btn:hover:not(:disabled) {
  background: #1FAD4F;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(37, 211, 102, 0.3);
}

.send-template-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.btn-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

/* Image Viewer Modal Styles */
.image-viewer-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.97);
  backdrop-filter: blur(10px);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeIn 0.3s ease;
  cursor: zoom-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.image-viewer-content {
  position: relative;
  width: 95vw;
  height: 95vh;
  display: flex;
  flex-direction: column;
  cursor: default;
}

/* Top Controls Bar */
.image-viewer-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.8) 0%, transparent 100%);
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 2;
}

.controls-left,
.controls-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.image-label {
  font-size: 16px;
  font-weight: 600;
  color: white;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
}

.image-viewer-btn {
  width: 40px;
  height: 40px;
  border: none;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(20px);
  color: white;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.image-viewer-btn:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.image-viewer-btn:active {
  transform: translateY(0);
}

/* Image Container */
.image-container {
  position: absolute;
  top: 80px;
  left: 40px;
  right: 40px;
  bottom: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.viewer-image {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.7);
  cursor: zoom-in;
  animation: zoomIn 0.3s ease;
}

@keyframes zoomIn {
  from {
    transform: scale(0.95);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

.message-img {
  cursor: zoom-in;
  transition: all 0.2s ease;
}

.message-img:hover {
  opacity: 0.85;
  transform: scale(1.02);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .image-viewer-controls {
    padding: 16px;
  }

  .image-label {
    font-size: 14px;
  }

  .image-viewer-btn {
    width: 36px;
    height: 36px;
  }

  .image-viewer-btn svg {
    width: 18px;
    height: 18px;
  }

  .image-container {
    top: 70px;
    left: 20px;
    right: 20px;
    bottom: 70px;
  }
}

/* Forward Message Styles */
.message-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 4px;
  gap: 8px;
}

.reply-btn,
.quick-note-btn {
  height: 24px;
  padding: 0 10px;
  border: 1px solid transparent;
  background: transparent;
  color: var(--text-secondary);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 500;
}

.reply-btn span,
.quick-note-btn span {
  line-height: 1;
}

.reply-btn:hover {
  background: rgba(37, 211, 102, 0.1);
  border-color: rgba(37, 211, 102, 0.3);
  color: #25D366;
  transform: translateY(-1px);
}

.quick-note-btn:hover {
  background: rgba(234, 179, 8, 0.1);
  border-color: rgba(234, 179, 8, 0.3);
  color: #ca8a04;
  transform: translateY(-1px);
}

.forward-btn {
  height: 24px;
  padding: 0 10px;
  border: 1px solid transparent;
  background: transparent;
  color: var(--text-secondary);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  flex-shrink: 0;
  margin-left: auto;
  font-size: 11px;
  font-weight: 500;
}

.forward-btn span {
  line-height: 1;
}

.forward-btn:hover {
  background: rgba(59, 130, 246, 0.1);
  border-color: rgba(59, 130, 246, 0.3);
  color: #3b82f6;
  transform: translateY(-1px);
}

/* Quoted message bubble inside chat */
.quoted-message {
  display: flex;
  flex-direction: column;
  border-radius: 6px;
  padding: 6px 10px;
  margin-bottom: 6px;
  border-left: 3px solid #25D366;
  background: rgba(0, 0, 0, 0.06);
  cursor: default;
  max-width: 100%;
  overflow: hidden;
}
.quoted-message.quoted-customer {
  border-left-color: #25D366;
}
.quoted-message.quoted-owner {
  border-left-color: #007bff;
}
.quoted-sender {
  font-size: 12px;
  font-weight: 700;
  color: #25D366;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.quoted-message.quoted-owner .quoted-sender {
  color: #007bff;
}
.quoted-preview {
  font-size: 12px;
  color: var(--text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Reply-to banner — styled as a mini chat bubble */
.reply-to-banner {
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  background: rgba(37, 211, 102, 0.08);
  border: 1px solid var(--border-primary);
  border-left: 4px solid #25D366;
  border-radius: 10px;
  padding: 8px 10px 8px 12px;
  margin-bottom: 6px;
  gap: 8px;
  animation: replyBannerIn 0.15s ease;
}

@keyframes replyBannerIn {
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
}

.reply-to-content {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
}

.reply-to-icon {
  flex-shrink: 0;
  color: #25D366;
  opacity: 0.8;
}

.reply-to-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 2px;
}

.reply-to-sender {
  font-size: 12px;
  font-weight: 700;
  color: #25D366;
  line-height: 1.3;
}

.reply-to-preview {
  font-size: 12px;
  color: var(--text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;
}

.reply-to-cancel {
  flex-shrink: 0;
  align-self: center;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-secondary);
  padding: 4px;
  display: flex;
  align-items: center;
  border-radius: 50%;
  transition: background 0.2s, color 0.2s;
}

.reply-to-cancel:hover {
  background: var(--hover-background);
  color: var(--text-primary);
}

/* Forward Modal */
.forward-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(5px);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeIn 0.2s ease;
}

.forward-modal-content {
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 16px;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.forward-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid var(--border-primary);
}

.forward-modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
}

.modal-close-btn {
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: var(--text-secondary);
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.modal-close-btn:hover {
  background: var(--hover-background);
  color: var(--text-primary);
}

.forward-message-preview {
  padding: 16px 24px;
  border-bottom: 1px solid var(--border-primary);
}

.preview-label {
  font-size: 12px;
  font-weight: 500;
  color: var(--text-secondary);
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.preview-content {
  background: var(--bg-secondary);
  border-radius: 8px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.preview-image {
  max-width: 200px;
  border-radius: 6px;
  overflow: hidden;
}

.preview-image img {
  width: 100%;
  height: auto;
  display: block;
}

.preview-document {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--primary-color);
  font-size: 14px;
}

.preview-text {
  margin: 0;
  color: var(--text-primary);
  font-size: 14px;
  line-height: 1.5;
  word-break: break-word;
}

.forward-search {
  padding: 16px 24px;
  border-bottom: 1px solid var(--border-primary);
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--bg-secondary);
}

.forward-search svg {
  color: var(--text-secondary);
  flex-shrink: 0;
}

.forward-search-input {
  flex: 1;
  border: none;
  background: transparent;
  outline: none;
  font-size: 14px;
  color: var(--text-primary);
}

.forward-search-input::placeholder {
  color: var(--text-secondary);
}

.forward-conversations-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
  min-height: 200px;
  max-height: 400px;
}

.forward-conversation-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 24px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  border-left: 3px solid transparent;
}

.forward-conversation-item:hover {
  background: var(--hover-background);
}

.forward-conversation-item.selected {
  background: rgba(37, 211, 102, 0.1);
  border-left: 3px solid #25D366;
}

.forward-conversation-item.current {
  opacity: 0.5;
  cursor: not-allowed;
}

.forward-conv-avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: var(--hover-background);
  color: var(--text-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 16px;
  flex-shrink: 0;
  border: 2px solid var(--border-primary);
}

.forward-conversation-item.selected .forward-conv-avatar {
  background: #25D366;
  color: white;
  border-color: #25D366;
}

.forward-conv-info {
  flex: 1;
  min-width: 0;
}

.forward-conv-name {
  font-weight: 500;
  font-size: 14px;
  color: var(--text-primary);
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.forward-conv-number {
  font-size: 12px;
  color: var(--text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.current-badge {
  font-size: 11px;
  padding: 4px 8px;
  background: var(--border-primary);
  color: var(--text-secondary);
  border-radius: 4px;
  font-weight: 500;
}

.selected-check {
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
}

.no-conversations {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: var(--text-secondary);
  text-align: center;
}

.no-conversations p {
  margin-top: 12px;
  font-size: 14px;
}

.forward-modal-footer {
  padding: 16px 24px;
  border-top: 1px solid var(--border-primary);
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
}

.btn-cancel,
.btn-forward {
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}

.btn-cancel {
  background: var(--hover-background);
  color: var(--text-primary);
  border: 1px solid var(--border-primary);
}

.btn-cancel:hover {
  background: var(--border-primary);
  color: var(--text-primary);
  transform: translateY(-1px);
}

.btn-forward {
  background: #25D366;
  color: white;
}

.btn-forward:hover:not(:disabled) {
  background: #1fbc5c;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(37, 211, 102, 0.4);
}

.btn-forward:disabled {
  background: var(--border-primary);
  color: var(--text-tertiary);
  cursor: not-allowed;
  opacity: 0.6;
}

/* Media Gallery Styles */
.media-gallery-btn {
  position: relative;
  padding: 8px 12px;
  background: var(--card-bg);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  color: var(--text-primary);
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 4px;
}

.media-gallery-btn:hover {
  background: var(--hover-bg);
  border-color: var(--border-hover);
}

.media-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  background: #25D366;
  color: white;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 10px;
  min-width: 18px;
  text-align: center;
}

.media-gallery-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 10000;
  display: flex;
  justify-content: flex-end;
  animation: fadeIn 0.2s;
}

.media-gallery-panel {
  width: 450px;
  max-width: 90vw;
  background: var(--bg-primary);
  height: 100vh;
  overflow-y: auto;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
  animation: slideInRight 0.3s;
  display: flex;
  flex-direction: column;
}

.media-gallery-header {
  padding: 20px;
  border-bottom: 1px solid var(--border-primary);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--bg-primary);
  position: sticky;
  top: 0;
  z-index: 10;
}

.media-gallery-header h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
}

.close-gallery-btn {
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s;
}

.close-gallery-btn:hover {
  background: var(--hover-bg);
  color: var(--text-primary);
}

.media-gallery-loading {
  padding: 40px 20px;
  text-align: center;
  color: var(--text-secondary);
  background: var(--bg-primary);
  flex: 1;
}

.media-tabs {
  display: flex;
  border-bottom: 1px solid var(--border-primary);
  background: var(--bg-primary);
  overflow-x: auto;
  flex-shrink: 0;
}

.media-gallery-content {
  flex: 1;
  overflow-y: auto;
}

.media-tab {
  flex: 1;
  padding: 12px 8px;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 13px;
  white-space: nowrap;
}

.media-tab:hover {
  background: var(--hover-bg);
  color: var(--text-primary);
}

.media-tab.active {
  color: #25D366;
  border-bottom-color: #25D366;
  font-weight: 500;
}

.media-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 8px;
  padding: 16px;
}

.media-item {
  position: relative;
  aspect-ratio: 1;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s;
  background: var(--hover-bg);
}

.media-item:hover {
  transform: scale(1.05);
  z-index: 2;
}

.media-item img,
.media-video-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.media-item-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  opacity: 0;
  transition: opacity 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.media-item:hover .media-item-overlay {
  opacity: 1;
}

.media-download-btn,
.media-play-btn {
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #333;
  transition: all 0.2s;
}

.media-download-btn:hover,
.media-play-btn:hover {
  background: white;
  transform: scale(1.1);
}

.media-item-date {
  position: absolute;
  bottom: 4px;
  left: 4px;
  right: 4px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  font-size: 10px;
  padding: 2px 4px;
  border-radius: 4px;
  text-align: center;
}

.media-list {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.media-audio-item,
.media-document-item {
  background: var(--hover-bg);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  transition: all 0.2s;
}

.media-audio-item:hover,
.media-document-item:hover {
  background: var(--card-bg);
  border-color: var(--border-hover);
}

.audio-info,
.document-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.audio-info svg,
.document-info svg {
  color: #25D366;
  flex-shrink: 0;
}

.audio-details,
.document-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.audio-label,
.document-name {
  font-weight: 500;
  color: var(--text-primary);
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.audio-date,
.document-date {
  font-size: 12px;
  color: var(--text-tertiary);
}

.audio-actions,
.document-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.audio-player {
  height: 32px;
  max-width: 200px;
}

.media-download-btn-small,
.document-view-btn {
  background: var(--card-bg);
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  padding: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--text-primary);
  transition: all 0.2s;
}

.media-download-btn-small:hover,
.document-view-btn:hover {
  background: var(--hover-bg);
  border-color: var(--border-hover);
  transform: translateY(-1px);
}

.no-media {
  padding: 60px 20px;
  text-align: center;
  color: var(--text-tertiary);
}

.no-media svg {
  margin-bottom: 12px;
}

.no-media p {
  margin: 0;
  font-size: 14px;
}

@keyframes slideInRight {
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@media (max-width: 768px) {
  .media-gallery-panel {
    width: 100vw;
    max-width: 100vw;
  }

  .media-grid {
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    gap: 6px;
    padding: 12px;
  }

  .media-tab {
    font-size: 11px;
    padding: 10px 6px;
  }

  .media-tab span {
    display: none;
  }

  .media-tab.active span {
    display: inline;
  }
}
</style>
